import React, { useState, useEffect, useRef } from 'react'
import { 
  Input, 
  Button, 
  Upload, 
  Spin, 
  Avatar, 
  Typography, 
  Dropdown, 
  Modal, 
  Input as AntInput,
  MenuProps,
  App,
} from 'antd'
import {
  SendOutlined,
  PaperClipOutlined,
  AudioOutlined,
  PictureOutlined,
  FileTextOutlined,
  UserOutlined,
  RobotOutlined,
  DeleteOutlined,
  HistoryOutlined,
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  PushpinOutlined,
  ShareAltOutlined,
  MoreOutlined,
  CloseOutlined,
  StopOutlined,
  BookOutlined,
  UnorderedListOutlined,
  CheckOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileMarkdownOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import type { UploadFile, UploadProps } from 'antd'
import { 
  startTextbookWriting,
  extractWritingDocumentText,
  buildMinimalBookOutlineFromTocText,
  confirmOutline, 
  generateChapter, 
  confirmChapter,
  downloadDocument,
  postWritingN8nProxy,
  // [已移除] getChapterStatus - 不再使用轮询
  type StartWritingResponse,
  type ConfirmOutlineRequest,
  type GenerateChapterRequest,
  type ConfirmChapterRequest,
  type ChapterStatusResponse
} from '../../api/textbooks'
import '../Chat/Chat.css'

const { TextArea } = Input
const { Text } = Typography
type ChatTextAreaRef = React.ComponentRef<typeof TextArea>

interface ChatMessage {
  id: string
  type: 'user' | 'assistant'
  content: string
  timestamp: number
  attachments?: Attachment[]
  // 交互式流程数据
  stage?: string  // 'outline_generated', 'outline_confirmed', 'chapter_generated', 'completed'
  bookOutline?: any  // 大纲数据
  outlineMarkdown?: string  // 大纲Markdown格式
  chapterNumber?: number  // 当前章节号
  chapterTitle?: string  // 当前章节标题
  chapterContent?: string  // 章节内容
  chapterMarkdown?: string  // 章节Markdown格式
}

interface Attachment {
  id: string
  type: 'image' | 'audio' | 'document'
  name: string
  url?: string
  data?: string
  mimeType: string
}

interface ChatSession {
  id: string
  title: string
  lastMessage: string
  timestamp: number
  messageCount: number
  pinned?: boolean
  group?: string
}

// 数字转中文函数
const numberToChinese = (num: number): string => {
  if (num === 0) return '零'
  const chineseNums = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十']
  if (num <= 10) {
    return chineseNums[num] || num.toString()
  } else if (num < 20) {
    return '十' + (num > 10 ? chineseNums[num % 10] : '')
  } else if (num < 100) {
    const tens = Math.floor(num / 10)
    const ones = num % 10
    return chineseNums[tens] + '十' + (ones > 0 ? chineseNums[ones] : '')
  }
  return num.toString()
}

// 格式化章节标题显示：第X章 章节名称（绪论用 chapterTitle 如「绪论」）
const formatChapterTitle = (chapterNumber?: number, chapterTitle?: string): string => {
  if (chapterNumber === undefined || chapterNumber === null) return chapterTitle?.trim() || '未命名章节'
  if (chapterNumber === 0 && chapterTitle?.trim()) return chapterTitle.trim() // 绪论
  const chapterNumberChinese = numberToChinese(chapterNumber)
  if (!chapterTitle || chapterTitle.trim() === '') {
    return `第${chapterNumberChinese}章`
  }
  // 如果章节标题已经包含"第X章"，则只显示章节名称
  // 否则显示"第X章 章节名称"
  if (chapterTitle.includes('第') && chapterTitle.includes('章')) {
    return chapterTitle
  }
  return `第${chapterNumberChinese}章 ${chapterTitle}`
}

// 从章节内容首行解析实际章节编号与标题，使对话框头部与实际内容一致
const parseChapterFromContent = (content: string | undefined): { number: number; title: string } | null => {
  if (!content || typeof content !== 'string') return null
  const trimmed = content.trim()
  // 匹配 "第X章" 或 "第五章" 等（阿拉伯数字或中文数字）
  const arabicMatch = trimmed.match(/^第\s*(\d+)\s*章\s*(.*)$/m)
  if (arabicMatch) {
    const num = parseInt(arabicMatch[1], 10)
    const rest = (arabicMatch[2] || '').trim()
    return { number: num, title: rest || `第${numberToChinese(num)}章` }
  }
  const chineseToNum: Record<string, number> = {
    '零': 0, '一': 1, '二': 2, '三': 3, '四': 4, '五': 5, '六': 6, '七': 7, '八': 8, '九': 9,
    '十': 10, '十一': 11, '十二': 12, '十三': 13, '十四': 14, '十五': 15, '十六': 16, '十七': 17, '十八': 18, '十九': 19,
    '二十': 20, '二十一': 21, '二十二': 22, '二十三': 23, '二十四': 24, '二十五': 25, '二十六': 26, '二十七': 27, '二十八': 28, '二十九': 29,
    '三十': 30,
  }
  const chineseMatch = trimmed.match(/^第\s*([一二三四五六七八九十百零]+)\s*章\s*(.*)$/m)
  if (chineseMatch) {
    const cn = chineseMatch[1].replace(/\s/g, '')
    let num = chineseToNum[cn]
    if (num === undefined && cn.length === 1) num = chineseToNum[cn]
    if (num === undefined) {
      // 多位数中文：十、十一…十九、二十…
      if (cn === '十') num = 10
      else if (cn.startsWith('十')) num = 10 + (chineseToNum[cn.slice(1)] ?? 0)
      else if (cn.startsWith('二十')) num = 20 + (chineseToNum[cn.slice(2)] ?? 0)
      else if (cn.startsWith('三十')) num = 30 + (chineseToNum[cn.slice(2)] ?? 0)
    }
    if (num !== undefined) {
      const rest = (chineseMatch[2] || '').trim()
      return { number: num, title: rest || `第${numberToChinese(num)}章` }
    }
  }
  return null
}

// [重要] 安全的 message 调用工具函数
// React 18 要求在 effect 中调用，而不是在 render 中
const createSafeMessageCaller = (messageApi: any) => {
  return {
    error: (content: string | { content: string; duration?: number }, duration?: number) => {
      setTimeout(() => {
        if (typeof content === 'string') {
          messageApi.error({ content, duration: duration || 5 })
        } else {
          messageApi.error(content)
        }
      }, 0)
    },
    success: (content: string | { content: string; duration?: number }, duration?: number) => {
      setTimeout(() => {
        if (typeof content === 'string') {
          messageApi.success({ content, duration: duration || 3 })
        } else {
          messageApi.success(content)
        }
      }, 0)
    },
    warning: (content: string | { content: string; duration?: number }, duration?: number) => {
      setTimeout(() => {
        if (typeof content === 'string') {
          messageApi.warning({ content, duration: duration || 3 })
        } else {
          messageApi.warning(content)
        }
      }, 0)
    },
    info: (content: string | { content: string; duration?: number }, duration?: number) => {
      setTimeout(() => {
        if (typeof content === 'string') {
          messageApi.info({ content, duration: duration || 3 })
        } else {
          messageApi.info(content)
        }
      }, 0)
    },
    loading: (content: string | { content: string; duration?: number; key?: string }, duration?: number) => {
      // message.loading 返回一个 MessageInstance，用于关闭 loading
      // 由于使用了 key，后续的 success/error 会自动替换 loading，所以通常不需要手动关闭
      // 直接调用 messageApi.loading，不使用 setTimeout（因为 loading 是在事件处理函数中调用的，不是在 render 中）
      if (typeof content === 'string') {
        return messageApi.loading({ content, duration: duration || 0 })
      } else {
        return messageApi.loading(content)
      }
    },
    open: (config: any) => {
      setTimeout(() => {
        return messageApi.open(config)
      }, 0)
    },
    destroy: (key?: string) => {
      setTimeout(() => {
        messageApi.destroy(key)
      }, 0)
    }
  }
}

const TextbookWritingChatPage: React.FC = () => {
  const navigate = useNavigate()
  const { message: messageApi } = App.useApp()
  const message = createSafeMessageCaller(messageApi)
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      type: 'assistant',
      content: '您好！我是智能教材编写助手。请告诉我您想要编写的教材信息，例如：\n\n- 教材名称\n- 目标读者（本科/硕士/博士）\n- 学科分类（中医临床/中药/文化等）\n- 其他特殊要求\n\n我会根据您的需求，结合编写规则库，为您生成高质量的教材大纲和内容。',
      timestamp: Date.now(),
    },
  ])
  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [sessionId, setSessionId] = useState(() => `textbook_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`)
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [sidebarVisible, setSidebarVisible] = useState(true)
  const [renameModalVisible, setRenameModalVisible] = useState(false)
  const [renameSessionId, setRenameSessionId] = useState<string>('')
  const [renameValue, setRenameValue] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  
  // 交互式流程状态
  const [currentStage, setCurrentStage] = useState<'initial' | 'outline_generated' | 'outline_confirmed' | 'chapter_generating' | 'chapter_generated' | 'completed'>('initial')
  const [currentBookOutline, setCurrentBookOutline] = useState<any>(null)
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0)  // 当前正在生成的章节索引
  // [已移除] 不再使用轮询，直接等待工作流完成
  const [generatedChapters, setGeneratedChapters] = useState<Map<number, string>>(new Map())  // 已生成的章节内容
  const [outlineModificationModalVisible, setOutlineModificationModalVisible] = useState(false)
  const [outlineModificationText, setOutlineModificationText] = useState('')
  const [chapterModificationModalVisible, setChapterModificationModalVisible] = useState(false)
  const [chapterModificationText, setChapterModificationText] = useState('')
  const [currentChapterData, setCurrentChapterData] = useState<{number: number, title: string, content: string} | null>(null)
  
  // 可编辑内容状态
  const [editingOutlineContent, setEditingOutlineContent] = useState<string>('')
  const [editingChapterContent, setEditingChapterContent] = useState<string>('')
  const [isEditingOutline, setIsEditingOutline] = useState(false)
  const [isEditingChapter, setIsEditingChapter] = useState(false)
  
  // 拖拽相关状态
  const [outlineModalPosition, setOutlineModalPosition] = useState({ x: 0, y: 0 })
  const [chapterModalPosition, setChapterModalPosition] = useState({ x: 0, y: 0 })
  const outlineModalRef = useRef<HTMLDivElement>(null)
  const chapterModalRef = useRef<HTMLDivElement>(null)
  const outlineDragStartRef = useRef<{ x: number, y: number } | null>(null)
  const chapterDragStartRef = useRef<{ x: number, y: number } | null>(null)
  const chatTextareaRef = useRef<ChatTextAreaRef>(null)
  /** 当前一次「加载中」对应的 AbortController，用于停止生成（大纲 / 章节 / Agent 等） */
  const activeChatAbortRef = useRef<AbortController | null>(null)

  const beginChatRequest = () => {
    const c = new AbortController()
    activeChatAbortRef.current = c
    return c
  }

  const endChatRequest = (c: AbortController) => {
    if (activeChatAbortRef.current === c) {
      activeChatAbortRef.current = null
    }
  }

  const isRequestAborted = (err: unknown) => {
    const e = err as { code?: string; name?: string; message?: string }
    return (
      e?.code === 'ERR_CANCELED' ||
      e?.name === 'CanceledError' ||
      e?.name === 'AbortError' ||
      (typeof e?.message === 'string' && /canceled|aborted/i.test(e.message))
    )
  }

  const handleStopConversation = () => {
    activeChatAbortRef.current?.abort()
  }

  /** 豆包式工具栏：当前选中的快捷模式（仅 UI，关闭按钮清空） */
  const [activeWritingMode, setActiveWritingMode] = useState<
    'outline' | 'content' | 'cover' | null
  >(null)
  /** 输入框组件内联提示（替代顶层 message.warning 的引导类文案） */
  const [composerHint, setComposerHint] = useState<string | null>(null)

  // 大纲修改对话框拖拽处理
  const handleOutlineModalMouseDown = (e: React.MouseEvent) => {
    // 只在标题栏区域允许拖拽
    const target = e.target as HTMLElement
    if (target.closest('.ant-modal-header') || target.closest('.ant-modal-title')) {
      outlineDragStartRef.current = {
        x: e.clientX - outlineModalPosition.x,
        y: e.clientY - outlineModalPosition.y
      }
      e.preventDefault()
    }
  }
  
  // 章节修改对话框拖拽处理
  const handleChapterModalMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('.ant-modal-header') || target.closest('.ant-modal-title')) {
      chapterDragStartRef.current = {
        x: e.clientX - chapterModalPosition.x,
        y: e.clientY - chapterModalPosition.y
      }
      e.preventDefault()
    }
  }
  
  // 监听鼠标移动和释放事件
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (outlineDragStartRef.current) {
        setOutlineModalPosition({
          x: e.clientX - outlineDragStartRef.current.x,
          y: e.clientY - outlineDragStartRef.current.y
        })
      }
      if (chapterDragStartRef.current) {
        setChapterModalPosition({
          x: e.clientX - chapterDragStartRef.current.x,
          y: e.clientY - chapterDragStartRef.current.y
        })
      }
    }
    
    const handleMouseUp = () => {
      outlineDragStartRef.current = null
      chapterDragStartRef.current = null
    }
    
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])
  
  // 重置位置当对话框关闭时
  useEffect(() => {
    if (!outlineModificationModalVisible) {
      setOutlineModalPosition({ x: 0, y: 0 })
      outlineDragStartRef.current = null
    }
  }, [outlineModificationModalVisible])
  
  useEffect(() => {
    if (!chapterModificationModalVisible) {
      setChapterModalPosition({ x: 0, y: 0 })
      chapterDragStartRef.current = null
    }
  }, [chapterModificationModalVisible])

  // 加载会话列表
  useEffect(() => {
    const sessionList = JSON.parse(localStorage.getItem('textbook_chat_sessions') || '[]')
    const sortedSessions = sessionList.sort((a: ChatSession, b: ChatSession) => {
      if (a.pinned && !b.pinned) return -1
      if (!a.pinned && b.pinned) return 1
      return b.timestamp - a.timestamp
    })
    setSessions(sortedSessions)
  }, [])

  // 加载聊天历史记录
  useEffect(() => {
    const savedMessages = localStorage.getItem(`textbook_chat_history_${sessionId}`)
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages)
        setMessages(parsed)
      } catch (e) {
        console.error('加载聊天历史失败:', e)
      }
    } else {
      setMessages([
        {
          id: '1',
          type: 'assistant',
          content: '您好！我是智能教材编写助手。请告诉我您想要编写的教材信息，例如：\n\n- 教材名称\n- 目标读者（本科/硕士/博士）\n- 学科分类（中医临床/中药/文化等）\n- 其他特殊要求\n\n我会根据您的需求，结合编写规则库，为您生成高质量的教材大纲和内容。',
          timestamp: Date.now(),
        },
      ])
    }
  }, [sessionId])

  // 保存聊天历史记录
  useEffect(() => {
    if (messages.length > 0) {
      try {
        const messagesToSave = messages.slice(-200)
        const messagesJson = JSON.stringify(messagesToSave)
        const sizeInBytes = new Blob([messagesJson]).size
        const sizeInMB = sizeInBytes / (1024 * 1024)
        
        if (sizeInMB > 4) {
          const limitedMessages = messages.slice(-100)
          localStorage.setItem(`textbook_chat_history_${sessionId}`, JSON.stringify(limitedMessages))
        } else {
          localStorage.setItem(`textbook_chat_history_${sessionId}`, messagesJson)
        }
        
        const sessionList = JSON.parse(localStorage.getItem('textbook_chat_sessions') || '[]')
        const sessionIndex = sessionList.findIndex((s: ChatSession) => s.id === sessionId)
        const existingSession = sessionIndex >= 0 ? sessionList[sessionIndex] : null
        // 标题优先使用第一条用户输入的内容，而非系统招呼语；若用户曾重命名则保留重命名
        const defaultGreeting = '您好！我是智能教材编写助手。'
        const isCustomTitle = existingSession?.title && !existingSession.title.startsWith('您好！我是智能教材编写助手')
        const firstUserMessage = messages.find(m => m.type === 'user')
        const titleFromUser = firstUserMessage?.content?.trim?.().substring(0, 30) || ''
        const sessionData: ChatSession = {
          id: sessionId,
          title: isCustomTitle ? existingSession!.title : (titleFromUser || existingSession?.title || '新对话'),
          lastMessage: messages[messages.length - 1]?.content?.substring(0, 50) || '',
          timestamp: Date.now(),
          messageCount: messages.length,
          pinned: existingSession?.pinned || false,
          group: existingSession?.group,
        }
        if (sessionIndex >= 0) {
          sessionList[sessionIndex] = sessionData
        } else {
          sessionList.unshift(sessionData)
        }
        const sortedSessions = sessionList
          .slice(0, 100)
          .sort((a: ChatSession, b: ChatSession) => {
            if (a.pinned && !b.pinned) return -1
            if (!a.pinned && b.pinned) return 1
            return b.timestamp - a.timestamp
          })
        localStorage.setItem('textbook_chat_sessions', JSON.stringify(sortedSessions))
        setSessions(sortedSessions)
      } catch (e: any) {
        if (e.name === 'QuotaExceededError' || e.code === 22) {
          console.error('Storage 配额超出，清理旧数据...')
          try {
            const sessionList = JSON.parse(localStorage.getItem('textbook_chat_sessions') || '[]')
            const recentSessions = sessionList.slice(0, 10)
            sessionList.slice(10).forEach((s: ChatSession) => {
              try {
                localStorage.removeItem(`textbook_chat_history_${s.id}`)
              } catch (err) {
                console.error(`删除会话 ${s.id} 的历史记录失败:`, err)
              }
            })
            localStorage.setItem('textbook_chat_sessions', JSON.stringify(recentSessions))
            setSessions(recentSessions)
            const limitedMessages = messages.slice(-50)
            localStorage.setItem(`textbook_chat_history_${sessionId}`, JSON.stringify(limitedMessages))
          } catch (cleanupError) {
            console.error('清理 Storage 失败:', cleanupError)
            message.warning('存储空间不足，部分聊天历史可能无法保存')
          }
        } else {
          console.error('保存聊天历史失败:', e)
        }
      }
    }
  }, [messages, sessionId])

  // 自动滚动到底部
  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // [已移除] 不再需要清理轮询定时器，因为不再使用轮询

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // 文件上传处理
  const handleUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError, onProgress } = options
    const fileObj = file as File & { uid?: string }

    try {
      const fileUid = (file as UploadFile).uid || Date.now().toString()
      const mimeType = fileObj.type || 'application/octet-stream'
      
      const attachment: Attachment = {
        id: Date.now().toString(),
        type: mimeType.startsWith('image/')
          ? 'image'
          : mimeType.startsWith('audio/')
          ? 'audio'
          : 'document',
        name: fileObj.name,
        mimeType: mimeType,
      }
      
      attachment.url = URL.createObjectURL(fileObj)

      const uploadFile: UploadFile = {
        uid: fileUid,
        name: fileObj.name,
        status: 'done',
        percent: 100,
        originFileObj: fileObj as any,
      }
      ;(uploadFile as any).attachment = attachment
      ;(uploadFile as any).fileObj = fileObj
      
      setFileList((prev) => {
        const existing = prev.find(f => f.uid === fileUid)
        if (existing) {
          return prev.map(f => f.uid === fileUid ? uploadFile : f)
        }
        return [...prev, uploadFile]
      })
      
      onProgress?.({ percent: 100 })
      onSuccess?.(attachment, fileObj as any)
    } catch (error) {
      const fileUid = (file as UploadFile).uid || Date.now().toString()
      setFileList((prev) => 
        prev.map(f => 
          f.uid === fileUid 
            ? { ...f, status: 'error' as const }
            : f
        )
      )
      onError?.(error as Error)
    }
  }

  // 移除文件
  const handleRemove = (file: UploadFile) => {
    setFileList((prev) => prev.filter((item) => item.uid !== file.uid))
  }

  // 发送消息
  const handleSend = async (customInput?: string) => {
    // 防止重复提交：如果正在加载中，直接返回
    if (loading) {
      console.log('[TextbookWriting] 正在处理中，忽略重复请求')
      return
    }
    
    // 使用自定义输入或当前输入值
    const finalInput = customInput || inputValue.trim()
    
    if (!finalInput && fileList.length === 0) {
      setComposerHint('请输入消息或上传文件')
      return
    }
    // 有文件时也必须输入文本，确保 chatInput 传给工作流
    if (fileList.length > 0 && !finalInput.trim()) {
      setComposerHint('上传文档时请同时输入教材描述（如书名、目标读者等）')
      return
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: finalInput || '已上传文件',
      timestamp: Date.now(),
      attachments: fileList.map((file) => {
        const attachment = (file as any).attachment as Attachment
        return attachment || {
          id: file.uid || Date.now().toString(),
          type: 'document',
          name: file.name || '文件',
          mimeType: file.type || 'application/octet-stream',
        }
      }),
    }

    const isOutlineMode = activeWritingMode === 'outline'
    const isContentMode = activeWritingMode === 'content'
    const shouldGenerateOutline =
      isOutlineMode || (!isContentMode && (currentStage === 'initial' || currentStage === 'completed'))

    // 非目录模式下：大纲已生成但未确认，仅记入对话
    if (!isOutlineMode && currentStage === 'outline_generated') {
      setMessages((prev) => [...prev, userMessage])
      if (!customInput) {
        setInputValue('')
      }
      setComposerHint(
        '已记录在对话中。如需按说明调整目录请用上方「对话修改」，准备好后请点击「确认大纲」。'
      )
      return
    }

    setMessages((prev) => [...prev, userMessage])
    const userInput = finalInput
    if (!customInput) {
      setInputValue('') // 只有在没有自定义输入时才清空输入框
    }
    const abortCtrl = beginChatRequest()
    setLoading(true) // 立即设置 loading，防止重复调用
    setComposerHint(null)

    try {
      // 根据当前阶段调用不同的API
      if (shouldGenerateOutline) {
        // 目录模式下可连续生成多本：每次目录生成前重置章节上下文，避免沿用旧书状态
        setCurrentStage('initial')
        setCurrentBookOutline(null)
        setGeneratedChapters(new Map())
        setCurrentChapterIndex(0)
        setCurrentChapterData(null)
        setEditingChapterContent('')
        setIsEditingChapter(false)

        // 收集参考文档文件（fileObj 为实际 File 对象）
        const referenceFiles = fileList
          .map((f) => (f as any).fileObj as File)
          .filter((f): f is File => !!f && f instanceof File)

        console.log('[TextbookWriting] 准备调用startTextbookWriting API', {
          chatInput: userInput,
          chatInputLength: (userInput || '').length,
          referenceFilesCount: referenceFiles.length,
          referenceFileNames: referenceFiles.map((f) => f.name)
        })

        const response = await startTextbookWriting(
          {
            chatInput: userInput,
            referenceFiles: referenceFiles.length > 0 ? referenceFiles : undefined,
          },
          { signal: abortCtrl.signal }
        )
        
        console.log('[TextbookWriting] API响应:', {
          success: response.success,
          message: response.message,
          stage: response.stage,
          hasBookOutline: !!response.bookOutline,
          suggestions: response.suggestions,
          error_details: response.error_details
        })

        if (response.success && response.stage === 'outline_generated' && response.bookOutline) {
          setCurrentBookOutline(response.bookOutline)
          setCurrentStage('outline_generated')
          // 初始化编辑内容
          setEditingOutlineContent(response.outlineMarkdown || '')
          
          const assistantMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            type: 'assistant',
            content: response.message || '大纲已生成',
            timestamp: Date.now(),
            stage: 'outline_generated',
            bookOutline: response.bookOutline,
            outlineMarkdown: response.outlineMarkdown,
          }

          setMessages((prev) => [...prev, assistantMessage])
          setComposerHint(null)
          message.success(
            isOutlineMode
              ? '目录已生成。目录模式已保持，您可继续输入下一本书并发送。'
              : '大纲已生成，请查看并确认'
          )
        } else if (!isOutlineMode) {
          // 构建详细的错误消息
          let errorMsg = response.message || '大纲生成失败'
          if (response.suggestions && response.suggestions.length > 0) {
            errorMsg += '\n\n建议：\n' + response.suggestions.join('\n')
          }
          // 添加错误详情到控制台（展开Object以便查看完整内容）
          console.error('[TextbookWriting] 大纲生成失败 - 完整响应:', JSON.stringify({
            success: response.success,
            message: response.message,
            stage: response.stage,
            suggestions: response.suggestions,
            error_details: response.error_details,
            taskId: response.taskId,
            workflowId: response.workflowId
          }, null, 2))
          throw new Error(errorMsg)
        } else {
          // 目录模式下后端返回异常时，透传错误信息
          const errorMsg = response.message || '目录生成失败'
          throw new Error(errorMsg)
        }
      } else if (isContentMode) {
        let contentTimedOut = false
        const tid = window.setTimeout(() => {
          contentTimedOut = true
          abortCtrl.abort()
        }, 600000)
        try {
          await runBookContentWorkflow(userInput, abortCtrl)
        } catch (e: unknown) {
          const err = e as { name?: string; message?: string }
          if (err?.name === 'AbortError') {
            if (contentTimedOut) {
              message.error('请求超时，请检查 n8n 与模型服务')
            } else {
              message.info('已停止生成')
            }
          } else {
            throw e
          }
        } finally {
          window.clearTimeout(tid)
        }
      } else {
        // 非目录/非正文模式：仅记入对话与引导
        setComposerHint(
          '当前为对话记录模式。生成目录请选「撰写书籍目录」，生成章节请点「撰写书籍内容」；撰写正文时请保持选中「撰写书籍内容」再点发送。'
        )
      }
    } catch (error: any) {
      if (isRequestAborted(error)) {
        message.info('已停止生成')
      } else {
      console.error('发送消息失败:', error)
      
      // [重要] 检查是否是 JSON 解析错误
      const isJsonParseError = error.message?.includes('Expecting value') || 
                               error.message?.includes('JSON') ||
                               error.message?.includes('Unexpected token') ||
                               (error.response?.data && typeof error.response.data === 'string' && !error.response.data.trim().startsWith('{'))
      
      if (isJsonParseError) {
        console.error('[TextbookWriting] JSON解析错误 - 这通常意味着后端返回了非JSON响应')
        console.error('[TextbookWriting] 错误详情:', {
          message: error.message,
          responseStatus: error.response?.status,
          responseHeaders: error.response?.headers,
          responseData: error.response?.data,
          responseDataType: typeof error.response?.data,
          responseDataPreview: typeof error.response?.data === 'string' ? error.response.data.substring(0, 200) : 'N/A'
        })
      } else {
        // [重要] 安全地序列化错误对象，避免循环引用
        const safeStringify = (obj: any, space?: number): string => {
          const seen = new WeakSet()
          return JSON.stringify(obj, (key, value) => {
            // 跳过循环引用
            if (typeof value === 'object' && value !== null) {
              if (seen.has(value)) {
                return '[Circular]'
              }
              seen.add(value)
            }
            // 跳过函数
            if (typeof value === 'function') {
              return '[Function]'
            }
            // 跳过 React 组件相关对象
            if (key === 'stateNode' || key === '_owner' || key === '_store') {
              return '[React Internal]'
            }
            return value
          }, space)
        }
        
        try {
          console.error('错误详情 - 完整对象:', safeStringify({
            message: error.message,
            responseStatus: error.response?.status,
            responseStatusText: error.response?.statusText,
            responseData: error.response?.data,
            errorType: error.name,
            errorStack: error.stack,
            errorCode: error.code,
            errorConfig: error.config ? {
              url: error.config.url,
              method: error.config.method,
              timeout: error.config.timeout
            } : undefined
          }, 2))
        } catch (stringifyError) {
          // 如果序列化仍然失败，只记录基本信息
          console.error('错误详情（简化）:', {
            message: error.message,
            name: error.name,
            stack: error.stack,
            responseStatus: error.response?.status,
            responseData: error.response?.data
          })
        }
      }
      
      // 构建详细的错误消息
      let errorContent = `抱歉，操作失败：${error.message || '未知错误'}`
      
      // 检查是否是 JSON 解析错误
      if (isJsonParseError) {
        errorContent = '后端返回了非JSON响应，可能是服务错误\n\n建议：\n1. 检查n8n服务是否运行: http://localhost:5678\n2. 检查工作流是否在n8n中激活\n3. 查看后端日志获取详细错误信息'
        // 如果响应数据是字符串，尝试显示预览
        if (error.response?.data && typeof error.response.data === 'string') {
          const preview = error.response.data.substring(0, 200)
          console.error('[TextbookWriting] 响应内容预览:', preview)
          if (preview.includes('<html>') || preview.includes('<!DOCTYPE')) {
            errorContent += '\n\n注意：后端返回了HTML页面，可能是错误页面'
          }
        }
      }
      // 检查是否是网络错误
      else if (error.message?.includes('timeout') || error.code === 'ECONNABORTED') {
        errorContent = '请求超时，请检查网络连接或稍后重试'
      } else if (error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
        errorContent = '网络错误，请检查：\n1. 后端服务是否运行\n2. 网络连接是否正常'
      }
      
      // 如果错误响应包含建议，添加到消息中
      if (error.response?.data?.suggestions && Array.isArray(error.response.data.suggestions) && error.response.data.suggestions.length > 0) {
        errorContent += '\n\n建议：\n' + error.response.data.suggestions.join('\n')
      } else if (error.suggestions && Array.isArray(error.suggestions) && error.suggestions.length > 0) {
        // 如果错误对象本身包含建议（从response.message中解析的）
        errorContent += '\n\n建议：\n' + error.suggestions.join('\n')
      } else if (error.response?.data?.error && error.response.data.suggestions && Array.isArray(error.response.data.suggestions)) {
        // 如果错误响应是 {error: true, suggestions: [...]} 格式
        errorContent += '\n\n建议：\n' + error.response.data.suggestions.join('\n')
      }
      
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: errorContent,
        timestamp: Date.now(),
      }
      setMessages((prev) => [...prev, errorMessage])
      
      // [重要] 使用安全的 message 调用（已在 createSafeMessageCaller 中处理）
      const errorTitle = error.response?.data?.message || error.message || '操作失败'
      message.error(errorTitle, 5)
      }
    } finally {
      endChatRequest(abortCtrl)
      setLoading(false)
    }
  }

  /** 与 Book Chapter Generation 对齐：下一待写章节（含绪论 chapterNumber=0） */
  const resolveNextChapterTaskForAgent = () => {
    if (!currentBookOutline) return null
    const intro = currentBookOutline.introduction
    const hasIntro = intro?.sections && intro.sections.length > 0
    if (hasIntro && !generatedChapters.has(0)) {
      return {
        chapterNumber: 0,
        chapterTitle: '绪论',
        sections: intro.sections || [],
      }
    }
    const chapters = currentBookOutline.chapters || []
    for (let i = 0; i < chapters.length; i++) {
      const ch = chapters[i]
      const num = ch.chapter_number ?? (i + 1)
      if (!generatedChapters.has(num)) {
        return {
          chapterNumber: num,
          chapterTitle: ch.chapter_title || `第${num}章`,
          sections: ch.sections || [],
        }
      }
    }
    return null
  }

  /** 直连 n8n「Book Chapter Generation」工作流（webhook `book-chapter-generation`），与工具栏「撰写书籍内容」同源 */
  const runBookChapterGenerationCore = async (
    chatInput: string,
    abortSignal: AbortSignal,
    options?: {
      outlineOverride?: any
      nextChapterOverride?: { chapterNumber: number; chapterTitle: string; sections: any[] }
      /** n8n「章节输入处理器」识别入口：目录工作流确认大纲 vs 上传目录抽最小大纲 */
      outlineSource?: 'from_outline_workflow' | 'direct_minimal'
      /** 路径2：从上传目录文档抽取的全文，供工作流引用（与 bookOutline 同源时可不传） */
      referenceDocumentText?: string
    }
  ) => {
    const outline = options?.outlineOverride ?? currentBookOutline
    const nextCh = options?.nextChapterOverride ?? resolveNextChapterTaskForAgent()
    if (!outline || !nextCh) {
      throw new Error('没有可撰写的章节任务')
    }
    const outlineSource = options?.outlineSource ?? 'from_outline_workflow'
    const refText = options?.referenceDocumentText?.trim()
    const body: Record<string, unknown> = {
      taskType: 'chapter' as const,
      chatInput: chatInput.trim() || '请撰写本章节教材正文，结构与条目与大纲一致。',
      bookOutline: outline,
      chapterNumber: nextCh.chapterNumber,
      chapterTitle: nextCh.chapterTitle,
      sections: nextCh.sections,
      sessionId,
      taskId: sessionId,
      outlineSource,
    }
    if (refText && refText.length > 0) {
      body.referenceDocumentText =
        refText.length > 100000 ? `${refText.slice(0, 100000)}\n…` : refText
    }
    const data = (await postWritingN8nProxy<Record<string, unknown>>(
      'book-chapter-generation',
      body
    )) as Record<string, unknown>
    if (data.error === true) {
      throw new Error(typeof data.message === 'string' ? data.message : '工作流参数错误')
    }
    const content = data.chapterContent as string | undefined
    if (
      data.success &&
      data.stage === 'chapter_generated' &&
      content &&
      content.length > 100
    ) {
      const chNum = data.chapterNumber as number
      const chTitle = (data.chapterTitle as string) || nextCh.chapterTitle
      const md = (data.chapterMarkdown as string) || content
      setEditingChapterContent(md)
      const chapterMessage: ChatMessage = {
        id: `chapter-agent-${chNum}-${Date.now()}`,
        type: 'assistant',
        content:
          (data.message as string) ||
          `第${chNum}章已生成，请查看并确认。`,
        timestamp: Date.now(),
        stage: 'chapter_generated',
        chapterNumber: chNum,
        chapterTitle: chTitle,
        chapterContent: content,
        chapterMarkdown: md,
      }
      setMessages((prev) => [...prev, chapterMessage])
      setCurrentChapterData({
        number: chNum,
        title: chTitle,
        content,
      })
      setGeneratedChapters((prev) => new Map(prev.set(chNum, content)))
      setCurrentStage('chapter_generated')
      setActiveWritingMode(null)
      setComposerHint(null)
      setTimeout(() => scrollToBottom(), 100)
      message.success(`第${chNum}章「${chTitle}」正文已生成`)
    } else {
      throw new Error(typeof data.message === 'string' ? data.message : '章节生成失败或内容过短')
    }
  }

  /**
   * 正文模式：无大纲时用后端 extract-document 提取目录文本并生成最小 bookOutline（不调用大纲 n8n），再直连 book-chapter-generation。
   * 供「撰写书籍内容」工具栏与选中文体模式后点「发送」共用。
   */
  const runBookContentWorkflow = async (chatInput: string, abortCtrl: AbortController) => {
    const signal = abortCtrl.signal
    let outlineForWriting = currentBookOutline
    let stageForWriting = currentStage
    let outlineSource: 'from_outline_workflow' | 'direct_minimal' = 'from_outline_workflow'
    let referenceDocumentTextForChapter: string | undefined

    if (!outlineForWriting) {
      const referenceFiles = fileList
        .map((f) => (f as any).fileObj as File)
        .filter((f): f is File => !!f && f instanceof File)
      if (referenceFiles.length === 0) {
        throw new Error('请先上传目录文档，或先用「撰写书籍目录」生成并确认大纲')
      }
      setComposerHint('正在从目录文档提取结构（直连章节工作流，不调用大纲工作流）…')
      const file = referenceFiles[0]
      const { text } = await extractWritingDocumentText(file, { signal })
      if (!text || text.length < 20) {
        throw new Error('未能从上传文档中提取足够文本，请检查文件格式或换用「撰写书籍目录」')
      }
      const titleFromName = file.name.replace(/\.[^.]+$/, '') || '教材'
      outlineForWriting = buildMinimalBookOutlineFromTocText(text, titleFromName)
      outlineSource = 'direct_minimal'
      referenceDocumentTextForChapter = text
      setCurrentBookOutline(outlineForWriting)
      setEditingOutlineContent(text.length > 12000 ? `${text.slice(0, 12000)}…` : text)
      setCurrentStage('outline_confirmed')
      stageForWriting = 'outline_confirmed'
      setComposerHint('已根据目录文本建立写作结构，正在调用章节撰写工作流…')
    }

    if (stageForWriting === 'outline_generated') {
      throw new Error('请先在页面中确认大纲后再撰写正文')
    }
    if (
      stageForWriting !== 'outline_confirmed' &&
      stageForWriting !== 'chapter_generating' &&
      stageForWriting !== 'chapter_generated' &&
      stageForWriting !== 'completed'
    ) {
      throw new Error('当前阶段无法撰写正文，请先完成目录生成与确认')
    }

    const resolveNext = (outline: any) => {
      if (!outline) return null
      const intro = outline.introduction
      const hasIntro = intro?.sections && intro.sections.length > 0
      if (hasIntro && !generatedChapters.has(0)) {
        return {
          chapterNumber: 0,
          chapterTitle: '绪论',
          sections: intro.sections || [],
        }
      }
      const chapters = outline.chapters || []
      for (let i = 0; i < chapters.length; i++) {
        const ch = chapters[i]
        const num = ch.chapter_number ?? (i + 1)
        if (!generatedChapters.has(num)) {
          return {
            chapterNumber: num,
            chapterTitle: ch.chapter_title || `第${num}章`,
            sections: ch.sections || [],
          }
        }
      }
      return null
    }

    const nextCh = resolveNext(outlineForWriting)
    if (!nextCh) {
      throw new Error('没有待撰写的章节，或已全部生成')
    }

    const stageSnapshot = currentStage
    setComposerHint(null)
    setCurrentStage('chapter_generating')
    try {
      await runBookChapterGenerationCore(
        chatInput.trim() || '请撰写本章节教材正文，结构与条目与大纲一致。',
        signal,
        {
          outlineOverride: outlineForWriting,
          nextChapterOverride: nextCh,
          outlineSource,
          referenceDocumentText: referenceDocumentTextForChapter,
        }
      )
    } catch (e) {
      setCurrentStage(stageSnapshot)
      setActiveWritingMode(null)
      setComposerHint(null)
      throw e
    }
  }

  /** 底部工具栏：撰写书籍目录 → 与发送一致，走后端 / n8n「Book Outline Generation」 */
  const handleToolbarWriteOutline = () => {
    if (loading) return
    setActiveWritingMode('outline')
    const text = inputValue.trim()
    if (!text && fileList.length === 0) {
      setComposerHint('请先在输入框中填写教材名称、读者与编写要求，或上传参考文档')
      return
    }
    setComposerHint('目录模式已启用：将仅生成目录。可连续输入不同书籍并发送。')
    handleSend()
  }

  /** 底部工具栏：撰写书籍内容 → 直连 n8n「Book Chapter Generation」（`/webhook/book-chapter-generation`） */
  const handleToolbarWriteContent = async () => {
    if (loading) return
    setActiveWritingMode('content')
    if (!currentBookOutline) {
      const referenceFiles = fileList
        .map((f) => (f as any).fileObj as File)
        .filter((f): f is File => !!f && f instanceof File)
      if (referenceFiles.length === 0) {
        setComposerHint('请先上传目录文档，或先生成大纲后再撰写正文')
        return
      }
    }
    const chatInput =
      inputValue.trim() || '请撰写本章节教材正文，结构与条目与大纲一致。'
    const abortCtrl = beginChatRequest()
    let agentTimedOut = false
    const tid = window.setTimeout(() => {
      agentTimedOut = true
      abortCtrl.abort()
    }, 600000)
    setLoading(true)
    try {
      await runBookContentWorkflow(chatInput, abortCtrl)
    } catch (e: unknown) {
      const err = e as { name?: string; message?: string }
      if (err?.name === 'AbortError') {
        if (agentTimedOut) {
          message.error('请求超时，请检查 n8n 与模型服务')
        } else {
          message.info('已停止生成')
        }
      } else {
        message.error(err?.message || '撰写失败，请确认 n8n 中「Book Chapter Generation」工作流已激活')
      }
    } finally {
      window.clearTimeout(tid)
      endChatRequest(abortCtrl)
      setLoading(false)
    }
  }

  /** 底部工具栏：生成书籍封面 → n8n「Book cover generate」webhook（onReceived 立即返回） */
  const handleToolbarWriteCover = async () => {
    setActiveWritingMode('cover')
    setComposerHint(null)
    try {
      message.loading({ content: '正在提交封面生成任务…', key: 'cover-wf', duration: 0 })
      await postWritingN8nProxy('book-cover-generate', {
        source: 'textbook-writing-chat',
      })
      message.destroy('cover-wf')
      message.success(
        '封面生成任务已提交（n8n：Book cover generate）。耗时较长时请在 n8n 执行列表中查看进度。'
      )
      setActiveWritingMode(null)
      setComposerHint(null)
    } catch (e: unknown) {
      message.destroy('cover-wf')
      setActiveWritingMode(null)
      setComposerHint(null)
      message.error(
        (e as Error)?.message ||
          '触发封面工作流失败，请确认 n8n 已启动且工作流已导入并激活'
      )
    }
  }

  // 确认大纲
  const handleConfirmOutline = async (modifications?: string, editedContent?: string) => {
    if (!currentBookOutline) {
      message.error('没有可确认的大纲')
      return
    }

    const abortCtrl = beginChatRequest()
    setLoading(true)
    try {
      // 如果有直接编辑的内容，使用编辑的内容；否则使用修改要求重新生成
      let finalModifications = modifications
      let updatedOutline = currentBookOutline
      let updatedMarkdown = ''
      
      if (editedContent && editedContent.trim()) {
        // 使用直接编辑的内容
        updatedMarkdown = editedContent
        setIsEditingOutline(false)
        message.success('大纲已更新（使用编辑内容）')
      } else if (modifications && modifications.trim()) {
        // 使用修改要求重新生成
        const response = await confirmOutline(
          {
            bookOutline: currentBookOutline,
            modifications: modifications,
            confirm: false, // 先不确认，只是重新生成
          },
          { signal: abortCtrl.signal }
        )
        
        if (response.success && response.bookOutline) {
          updatedOutline = response.bookOutline
          updatedMarkdown = response.outlineMarkdown || ''
          setCurrentBookOutline(updatedOutline)
          message.success('大纲已根据修改要求重新生成')
          
          // 更新消息列表，显示更新后的大纲
          setMessages((prev) => {
            const updated = [...prev]
            // 使用兼容的方法查找最后一条大纲消息
            let lastOutlineIndex = -1
            for (let i = updated.length - 1; i >= 0; i--) {
              if (updated[i].stage === 'outline_generated') {
                lastOutlineIndex = i
                break
              }
            }
            if (lastOutlineIndex >= 0) {
              updated[lastOutlineIndex] = {
                ...updated[lastOutlineIndex],
                bookOutline: updatedOutline,
                outlineMarkdown: updatedMarkdown,
              }
            }
            return updated
          })
        } else {
          throw new Error(response.message || '重新生成大纲失败')
        }
      } else {
        // 没有修改，使用原内容
        updatedMarkdown = editingOutlineContent || ''
      }
      
      // 如果用户确认，继续生成章节
      const confirmResponse = await confirmOutline(
        {
          bookOutline: updatedOutline,
          modifications: undefined,
          confirm: true,
        },
        { signal: abortCtrl.signal }
      )
      
      if (confirmResponse.success) {
        // [重要] 更新当前大纲为确认后的大纲（如果后端返回了更新后的大纲）
        if (confirmResponse.bookOutline) {
          setCurrentBookOutline(confirmResponse.bookOutline)
          console.log('[确认大纲] 已更新currentBookOutline:', {
            title: confirmResponse.bookOutline.title,
            chaptersCount: confirmResponse.bookOutline.chapters?.length || 0
          })
        } else {
          // 如果后端没有返回新大纲，使用已更新的outline
          setCurrentBookOutline(updatedOutline)
          console.log('[确认大纲] 使用updatedOutline:', {
            title: updatedOutline.title,
            chaptersCount: updatedOutline.chapters?.length || 0
          })
        }
        
        // [重要] 清空之前生成的章节缓存，确保使用新大纲生成新章节
        setGeneratedChapters(new Map())
        setCurrentChapterIndex(0)
        setCurrentChapterData(null)
        setEditingChapterContent('')
        console.log('[确认大纲] 已清空章节缓存，准备生成新章节')
        
        // [重要] 清除消息列表中的旧章节消息，只保留大纲相关的消息
        setMessages((prev) => {
          // 过滤掉所有章节生成相关的消息（stage === 'chapter_generated' 或 'chapter_generating'）
          const beforeCount = prev.length
          const filtered = prev.filter(msg => 
            msg.stage !== 'chapter_generated' && 
            msg.stage !== 'chapter_generating' &&
            !msg.chapterContent &&
            !msg.chapterMarkdown
          )
          console.log(`[确认大纲] 已清除${beforeCount - filtered.length}条旧章节消息`)
          console.log(`[确认大纲] 保留${filtered.length}条消息（仅大纲相关）`)
          return filtered
        })
        
        setCurrentStage('outline_confirmed')
        setOutlineModificationModalVisible(false)
        setOutlineModificationText('')
        setIsEditingOutline(false)
        setEditingOutlineContent('')
        
        const confirmMessage: ChatMessage = {
          id: Date.now().toString(),
          type: 'assistant',
          content: '大纲已确认！现在开始生成章节内容。',
          timestamp: Date.now(),
          stage: 'outline_confirmed',
          bookOutline: confirmResponse.bookOutline || updatedOutline, // 保存确认后的大纲到消息中
        }
        setMessages((prev) => [...prev, confirmMessage])
        
        // [重要] 检查是否有绪论，如果有则先生成绪论
        const hasIntroduction = confirmResponse.bookOutline?.introduction?.sections?.length > 0 || updatedOutline?.introduction?.sections?.length > 0
        if (hasIntroduction) {
          message.success('大纲已确认，开始生成绪论')
          // 使用setTimeout确保state更新后再调用
          setTimeout(() => {
            handleGenerateIntroduction() // 先生成绪论
          }, 100)
        } else {
          message.success('大纲已确认，开始生成第一章')
          // 使用setTimeout确保state更新后再调用
          setTimeout(() => {
            handleGenerateNextChapter(0) // 从第一章开始（索引0）
          }, 100)
        }
      } else {
        throw new Error(confirmResponse.message || '确认大纲失败')
      }
    } catch (error: any) {
      if (isRequestAborted(error)) {
        message.info('已停止生成')
      } else {
        console.error('确认大纲失败:', error)
        message.error(`确认大纲失败：${error.message || '未知错误'}`)
      }
    } finally {
      endChatRequest(abortCtrl)
      setLoading(false)
    }
  }

  // 生成绪论
  const handleGenerateIntroduction = async () => {
    if (!currentBookOutline || !currentBookOutline.introduction) {
      // 如果没有绪论，直接生成第一章
      handleGenerateNextChapter(0)
      return
    }

    const introduction = currentBookOutline.introduction
    const introductionSections = introduction.sections || []
    
    // 检查是否已经生成过绪论
    if (generatedChapters.has(0)) {
      console.log('绪论已生成，跳过')
      // 跳过绪论，直接生成第一章
      handleGenerateNextChapter(0)
      return
    }
    
    const abortCtrl = beginChatRequest()
    setLoading(true)
    setCurrentStage('chapter_generating')

    try {
      // [简化] 直接等待工作流完成，不使用轮询
      console.log('[生成绪论] 调用generateChapter API，等待工作流完成...')
      const response = await generateChapter(
        {
          bookOutline: currentBookOutline, // [重要] 使用当前确认的大纲
          chapterNumber: 0, // 绪论使用0作为章节编号
          chapterTitle: '绪论',
          sections: introductionSections,
        },
        { signal: abortCtrl.signal }
      )
      
      console.log('[生成绪论] 工作流完成，收到响应:', {
        success: response.success,
        stage: response.stage,
        chapterNumber: response.chapterNumber,
        chapterTitle: response.chapterTitle,
        contentLength: response.chapterContent?.length || 0
      })
      
      // 检查响应是否成功
      if (response.success && response.stage === 'chapter_generated' && response.chapterContent && response.chapterContent.length > 100) {
        // 初始化编辑内容
        setEditingChapterContent(response.chapterMarkdown || response.chapterContent)
        
        const chapterMessage: ChatMessage = {
          id: `chapter-0-${Date.now()}`,
          type: 'assistant',
          content: response.message || '绪论已生成，请查看并确认。确认后可下载三种格式（Markdown、DOCX、PDF），确认后将继续生成第一章。',
          timestamp: Date.now(),
          stage: 'chapter_generated',
          chapterNumber: 0,
          chapterTitle: response.chapterTitle || '绪论',
          chapterContent: response.chapterContent,
          chapterMarkdown: response.chapterMarkdown || response.chapterContent,
        }

        // 始终追加新章节消息，使每一章都在页面中显示，不覆盖已有章节
        setMessages((prev) => {
          console.log('[生成绪论] 追加绪论消息，当前消息数:', prev.length)
          return [...prev, chapterMessage]
        })
        
        setCurrentChapterData({
          number: 0,
          title: response.chapterTitle || '绪论',
          content: response.chapterContent,
        })
        setGeneratedChapters(new Map(generatedChapters.set(0, response.chapterContent)))
        setCurrentStage('chapter_generated')
        
        // 自动滚动到新生成的章节
        setTimeout(() => {
          scrollToBottom()
        }, 100)
        
        message.success('绪论已生成！请查看上方章节内容，确认后可下载三种格式（Markdown、DOCX、PDF），确认后将自动生成第一章。')
      } else {
        throw new Error(response.message || '绪论生成失败：响应数据不完整')
      }
    } catch (error: any) {
      if (isRequestAborted(error)) {
        message.info('已停止生成')
        setCurrentStage('outline_confirmed')
      } else {
        console.error('生成绪论失败:', error)
        message.error(`生成绪论失败：${error.message || '未知错误'}`)
      }
    } finally {
      endChatRequest(abortCtrl)
      setLoading(false)
    }
  }

  // 生成下一章
  const handleGenerateNextChapter = async (targetIndex?: number) => {
    if (!currentBookOutline || !currentBookOutline.chapters) {
      message.error('缺少大纲信息')
      return
    }

    const chapters = currentBookOutline.chapters
    
    // 如果指定了目标索引，使用它；否则使用当前索引
    const currentIndex = targetIndex !== undefined ? targetIndex : currentChapterIndex
    
    if (currentIndex >= chapters.length) {
      message.success('所有章节已生成完成！')
      setCurrentStage('completed')
      return
    }

    const chapter = chapters[currentIndex]
    const chapterNumber = chapter.chapter_number || (currentIndex + 1)
    
    // 检查是否已经生成过该章节
    if (generatedChapters.has(chapterNumber)) {
      console.log(`第${chapterNumber}章已生成，跳过`)
      // 跳过已生成的章节，继续下一章
      const nextIndex = currentIndex + 1
      setCurrentChapterIndex(nextIndex)
      // 使用 setTimeout 避免在 setState 回调中直接调用异步函数
      setTimeout(() => {
        handleGenerateNextChapter(nextIndex)
      }, 0)
      return
    }
    
    const abortCtrl = beginChatRequest()
    setLoading(true)
    setCurrentStage('chapter_generating')

    try {
      // [调试] 打印传递给章节生成API的大纲信息
      console.log('[生成章节] 准备调用generateChapter API:', {
        bookOutlineTitle: currentBookOutline?.title,
        bookOutlineChaptersCount: currentBookOutline?.chapters?.length || 0,
        chapterNumber: chapterNumber,
        chapterTitle: chapter.chapter_title || `第${chapterNumber}章`,
        sectionsCount: chapter.sections?.length || 0,
        firstSectionTitle: chapter.sections?.[0]?.section_title || chapter.sections?.[0] || 'N/A'
      })
      
      // [简化] 直接等待工作流完成，不使用轮询
      console.log('[生成章节] 调用generateChapter API，等待工作流完成...')
      const response = await generateChapter(
        {
          bookOutline: currentBookOutline, // [重要] 使用当前确认的大纲
          chapterNumber: chapterNumber,
          chapterTitle: chapter.chapter_title || `第${chapterNumber}章`,
          sections: chapter.sections || [],
        },
        { signal: abortCtrl.signal }
      )
      
      console.log('[生成章节] 工作流完成，收到响应:', {
        success: response.success,
        stage: response.stage,
        chapterNumber: response.chapterNumber,
        chapterTitle: response.chapterTitle,
        contentLength: response.chapterContent?.length || 0
      })
      
      // 检查响应是否成功
      if (response.success && response.stage === 'chapter_generated' && response.chapterContent && response.chapterContent.length > 100) {
        // 初始化编辑内容
        setEditingChapterContent(response.chapterMarkdown || response.chapterContent)
        
        const chapterMessage: ChatMessage = {
          id: `chapter-${response.chapterNumber}-${Date.now()}`,
          type: 'assistant',
          content: response.message || `第${response.chapterNumber}章已生成，请查看并确认。确认后可下载三种格式（Markdown、DOCX、PDF），确认后将继续生成下一章。`,
          timestamp: Date.now(),
          stage: 'chapter_generated',
          chapterNumber: response.chapterNumber,
          chapterTitle: response.chapterTitle || chapter.chapter_title || `第${chapterNumber}章`,
          chapterContent: response.chapterContent,
          chapterMarkdown: response.chapterMarkdown || response.chapterContent,
        }

        // 始终追加新章节消息，使每一章都在页面中显示，不覆盖已有章节
        setMessages((prev) => {
          console.log(`[生成章节] 追加第${response.chapterNumber}章消息，当前消息数:`, prev.length)
          return [...prev, chapterMessage]
        })
        
        setCurrentChapterData({
          number: response.chapterNumber,
          title: response.chapterTitle || chapter.chapter_title || `第${chapterNumber}章`,
          content: response.chapterContent,
        })
        setGeneratedChapters(new Map(generatedChapters.set(response.chapterNumber, response.chapterContent)))
        // ⚠️ 重要：不自动更新索引，等待用户确认后再继续下一章
        setCurrentStage('chapter_generated')
        
        // 自动滚动到新生成的章节
        setTimeout(() => {
          scrollToBottom()
        }, 100)
        
        message.success(`第${response.chapterNumber}章「${response.chapterTitle || ''}」已生成！请查看上方章节内容，确认后可下载三种格式（Markdown、DOCX、PDF），确认后将自动生成下一章。`)
      } else {
        throw new Error(response.message || `第${chapterNumber}章生成失败：响应数据不完整`)
      }
    } catch (error: any) {
      if (isRequestAborted(error)) {
        message.info('已停止生成')
        setCurrentStage('chapter_generated')
      } else {
        console.error('生成章节失败:', error)
        // 如果错误是"章节已生成"，跳过该章节
        if (error.message && error.message.includes('已生成')) {
          const nextIndex = currentIndex + 1
          setCurrentChapterIndex(nextIndex)
          setTimeout(() => {
            handleGenerateNextChapter(nextIndex)
          }, 0)
        } else {
          message.error(`生成章节失败：${error.message || '未知错误'}`)
        }
      }
    } finally {
      endChatRequest(abortCtrl)
      setLoading(false)
    }
  }

  // 确认章节并继续下一章
  const handleConfirmChapter = async (modifications?: string, continueNext: boolean = true, editedContent?: string) => {
    if (!currentChapterData) {
      message.error('没有可确认的章节')
      return
    }

    const abortCtrl = beginChatRequest()
    setLoading(true)
    try {
      // 如果有直接编辑的内容，使用编辑的内容；否则使用修改要求重新生成
      let updatedContent = currentChapterData.content
      let updatedMarkdown = ''
      
      if (editedContent && editedContent.trim()) {
        // 使用直接编辑的内容
        updatedContent = editedContent
        updatedMarkdown = editedContent
        setIsEditingChapter(false)
        message.success('章节已更新（使用编辑内容）')
      } else if (modifications && modifications.trim()) {
        // 使用修改要求重新生成
        const response = await confirmChapter(
          {
            chapterNumber: currentChapterData.number,
            chapterContent: currentChapterData.content,
            modifications: modifications,
            confirm: false, // 先不确认，只是重新生成
            continueNext: false,
            bookOutline: currentBookOutline,
          },
          { signal: abortCtrl.signal }
        )
        
        if (response.success && response.chapterContent) {
          updatedContent = response.chapterContent
          updatedMarkdown = response.chapterMarkdown || response.chapterContent
          setCurrentChapterData({
            ...currentChapterData,
            content: updatedContent,
          })
          message.success('章节已根据修改要求重新生成')
        } else {
          throw new Error(response.message || '重新生成章节失败')
        }
      } else {
        // 没有修改，使用原内容
        updatedContent = currentChapterData.content
        updatedMarkdown = editingChapterContent || currentChapterData.content
      }
      
      // 更新消息列表，显示更新后的章节
      if (updatedMarkdown) {
        setMessages((prev) => {
          const updated = [...prev]
          // 使用兼容的方法查找最后一条对应章节的消息
          let lastChapterIndex = -1
          for (let i = updated.length - 1; i >= 0; i--) {
            if (updated[i].stage === 'chapter_generated' && updated[i].chapterNumber === currentChapterData.number) {
              lastChapterIndex = i
              break
            }
          }
          if (lastChapterIndex >= 0) {
            updated[lastChapterIndex] = {
              ...updated[lastChapterIndex],
              chapterContent: updatedContent,
              chapterMarkdown: updatedMarkdown,
            }
          }
          return updated
        })
        
        // 更新已生成的章节映射
        setGeneratedChapters(new Map(generatedChapters.set(currentChapterData.number, updatedContent)))
      }
      
      // 如果用户确认，继续下一章
      const confirmResponse = await confirmChapter(
        {
          chapterNumber: currentChapterData.number,
          chapterContent: updatedContent,
          modifications: undefined,
          confirm: true,
          continueNext: continueNext,
          bookOutline: currentBookOutline,
        },
        { signal: abortCtrl.signal }
      )

      if (confirmResponse.success) {
        setChapterModificationModalVisible(false)
        setChapterModificationText('')
        setIsEditingChapter(false)
        setEditingChapterContent('')
        
        const confirmedChapterNumber = currentChapterData?.number || 0
        
        setCurrentChapterData(null)

        if (continueNext && !confirmResponse.completed) {
          // 继续生成下一章
          // ⚠️ 重要：确认后才更新索引并继续下一章
          setCurrentChapterIndex(prev => {
            const nextIndex = prev + 1
            console.log(`用户已确认第${confirmedChapterNumber}章，准备生成第${nextIndex + 1}章`)
            return nextIndex
          })
          message.success({
            content: `第${confirmedChapterNumber}章已确认，开始生成下一章...`,
            duration: 3,
          })
          // 短暂延迟后开始生成下一章，让用户看到确认消息
          setTimeout(async () => {
            // 如果当前章节是绪论（chapterNumber=0），生成第一章；否则生成下一章
            if (confirmedChapterNumber === 0) {
              await handleGenerateNextChapter(0) // 从第一章开始（索引0）
            } else {
          await handleGenerateNextChapter()
            }
          }, 500)
        } else {
          // 仅确认，不继续下一章
          message.success('章节已确认')
          // 检查是否所有章节都已生成
          if (currentBookOutline && currentBookOutline.chapters) {
            const totalChapters = currentBookOutline.chapters.length
            const generatedCount = generatedChapters.size
            if (generatedCount >= totalChapters) {
          // 所有章节完成
          setCurrentStage('completed')
          const completeMessage: ChatMessage = {
            id: Date.now().toString(),
            type: 'assistant',
            content: '所有章节已生成完成！您可以下载完整书籍。',
            timestamp: Date.now(),
            stage: 'completed',
          }
          setMessages((prev) => [...prev, completeMessage])
          message.success('书籍生成完成！')
            }
          }
        }
      } else {
        throw new Error(confirmResponse.message || '确认章节失败')
      }
    } catch (error: any) {
      if (isRequestAborted(error)) {
        message.info('已停止生成')
      } else {
        console.error('确认章节失败:', error)
        message.error(`确认章节失败：${error.message || '未知错误'}`)
      }
    } finally {
      endChatRequest(abortCtrl)
      setLoading(false)
    }
  }

  // 下载文档
  const handleDownloadDocument = async (
    type: 'outline' | 'chapter' | 'book', 
    format: 'markdown' | 'docx' | 'pdf' = 'markdown',
    options?: {
      content?: string
      chapterNumber?: number
      chapterTitle?: string
      title?: string
      author?: string
    }
  ) => {
    try {
      message.loading({ content: '正在生成文档...', key: 'download' })
      
      let blob: Blob
      
      if (type === 'chapter' && options?.content) {
        // 下载单个章节
        blob = await downloadDocument('chapter', format, {
          content: options.content,
          title: options.chapterTitle || `第${options.chapterNumber}章`,
          author: options.author || currentBookOutline?.author,
          chapterNumber: options.chapterNumber,
        })
      } else if (type === 'book' && generatedChapters.size > 0) {
        // 下载完整书籍（合并所有章节，包含大纲）
        const chapters = Array.from(generatedChapters.entries())
          .sort(([a], [b]) => a - b)
          .map(([chapterNumber, content]) => {
            // 从消息中查找章节标题
            const chapterMsg = messages.find(
              m => m.stage === 'chapter_generated' && m.chapterNumber === chapterNumber
            )
            return {
              chapterNumber,
              chapterTitle: chapterMsg?.chapterTitle || `第${chapterNumber}章`,
              chapterContent: content,
              chapterMarkdown: content,
            }
          })
        
        // 查找大纲内容
        const outlineMsg = messages.find(m => m.stage === 'outline_generated')
        const outlineMarkdown = outlineMsg?.outlineMarkdown || ''
        
        blob = await downloadDocument('book', format, {
          chapters,
          title: currentBookOutline?.title || '完整书籍',
          author: currentBookOutline?.author,
          outlineMarkdown: outlineMarkdown,  // 传递大纲内容
        })
      } else if (type === 'outline' && options?.content) {
        // 下载大纲
        blob = await downloadDocument('outline', format, {
          content: options.content,
          title: options.title || currentBookOutline?.title,
          author: options.author || currentBookOutline?.author,
        })
      } else {
        // 从存储中获取（使用 taskId）
        blob = await downloadDocument(type, format, {
          taskId: sessionId,
          chapterNumber: options?.chapterNumber,
        })
      }
      
      // 创建下载链接
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      
      // 确定文件名：书籍名称 + 章编号 + 章名称（章节下载时）
      const bookTitle = currentBookOutline?.title || options?.title || '未命名教材'
      const safeTitle = bookTitle.replace(/[/\\:*?"<>|]/g, '_').substring(0, 50)  // 移除非法字符并限制长度
      let filename = ''
      if (type === 'outline') {
        filename = `${safeTitle}_大纲.${format === 'markdown' ? 'md' : format}`
      } else if (type === 'chapter') {
        const chNum = options?.chapterNumber ?? 1
        const chTitle = (options?.chapterTitle || '').trim().replace(/[/\\:*?"<>|]/g, '_').substring(0, 30)
        const ext = format === 'markdown' ? 'md' : format
        if (chNum === 0) {
          filename = `${safeTitle}_绪论.${ext}`
        } else {
          filename = chTitle ? `${safeTitle}_第${chNum}章_${chTitle}.${ext}` : `${safeTitle}_第${chNum}章.${ext}`
        }
      } else {
        filename = `${safeTitle}.${format === 'markdown' ? 'md' : format}`
      }
      
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      message.success({ content: '下载成功', key: 'download' })
    } catch (error: any) {
      console.error('下载文档失败:', error)
      message.error({ content: `下载失败：${error.message || '未知错误'}`, key: 'download' })
    }
  }

  // 键盘快捷键
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // 语音录制相关函数（简化版，参考Chat页面）
  const handleStartRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        message.error('您的浏览器不支持语音录制功能')
        return
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        } 
      })
      
      let mimeType = 'audio/webm;codecs=opus'
      const supportedTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4',
        'audio/wav',
      ]
      
      for (const type of supportedTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type
          break
        }
      }
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }
      
      mediaRecorder.onstop = () => {
        if (audioChunksRef.current.length === 0) {
          message.warning('录制失败，未获取到音频数据')
          stream.getTracks().forEach(track => track.stop())
          return
        }
        
        const extension = mimeType.includes('webm') ? 'webm' 
          : mimeType.includes('ogg') ? 'ogg'
          : mimeType.includes('mp4') ? 'm4a'
          : mimeType.includes('wav') ? 'wav'
          : 'webm'
        
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType })
        const audioFile = new File([audioBlob], `voice_${Date.now()}.${extension}`, {
          type: mimeType,
          lastModified: Date.now(),
        })
        
        const attachment: Attachment = {
          id: Date.now().toString(),
          type: 'audio',
          name: audioFile.name,
          mimeType: mimeType,
          url: URL.createObjectURL(audioBlob),
        }
        
        const fileUid = Date.now().toString()
        const uploadFile: UploadFile = {
          uid: fileUid,
          name: audioFile.name,
          status: 'done',
          percent: 100,
          originFileObj: audioFile as any,
        }
        
        ;(uploadFile as any).attachment = attachment
        ;(uploadFile as any).fileObj = audioFile
        
        setFileList((prev) => [...prev, uploadFile])
        stream.getTracks().forEach(track => track.stop())
        message.success('语音录制完成')
      }
      
      mediaRecorder.start(1000)
      setIsRecording(true)
      setRecordingTime(0)
      
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1)
      }, 1000)
      
      message.success('开始录制语音...')
    } catch (error: any) {
      console.error('录制过程发生错误:', error)
      message.error(`录制失败: ${error.message || '未知错误'}`)
    }
  }
  
  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current)
        recordingTimerRef.current = null
      }
      
      setRecordingTime(0)
    }
  }

  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current)
      }
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop()
      }
    }
  }, [isRecording])

  // 创建新对话
  const handleNewChat = () => {
    const newSessionId = `textbook_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setSessionId(newSessionId)
    setMessages([
      {
        id: '1',
        type: 'assistant',
        content: '您好！我是智能教材编写助手。请告诉我您想要编写的教材信息，例如：\n\n- 教材名称\n- 目标读者（本科/硕士/博士）\n- 学科分类（中医临床/中药/文化等）\n- 其他特殊要求\n\n我会根据您的需求，结合编写规则库，为您生成高质量的教材大纲和内容。',
        timestamp: Date.now(),
      },
    ])
    setInputValue('')
    setFileList([])
  }

  // 切换会话
  const handleSwitchSession = (id: string) => {
    setSessionId(id)
    setInputValue('')
    setFileList([])
  }

  // 获取分组后的会话列表
  const getGroupedSessions = () => {
    const filtered = searchQuery
      ? sessions.filter(s => 
          s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : sessions

    const now = Date.now()
    const today = new Date().setHours(0, 0, 0, 0)
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000

    const pinned = filtered.filter(s => s.pinned)
    const todaySessions = filtered.filter(s => !s.pinned && s.timestamp >= today)
    const weekSessions = filtered.filter(s => !s.pinned && s.timestamp >= sevenDaysAgo && s.timestamp < today)
    const monthSessions = filtered.filter(s => !s.pinned && s.timestamp >= thirtyDaysAgo && s.timestamp < sevenDaysAgo)
    const olderSessions = filtered.filter(s => !s.pinned && s.timestamp < thirtyDaysAgo)

    const monthlyGroups: { [key: string]: ChatSession[] } = {}
    olderSessions.forEach(session => {
      const date = new Date(session.timestamp)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      if (!monthlyGroups[monthKey]) {
        monthlyGroups[monthKey] = []
      }
      monthlyGroups[monthKey].push(session)
    })

    return { pinned, todaySessions, weekSessions, monthSessions, monthlyGroups }
  }

  // 重命名会话
  const handleRename = (id: string) => {
    const session = sessions.find(s => s.id === id)
    setRenameSessionId(id)
    setRenameValue(session?.title || '')
    setRenameModalVisible(true)
  }

  const handleRenameConfirm = () => {
    if (!renameValue.trim()) {
      message.warning('请输入会话名称')
      return
    }
    const sessionList = JSON.parse(localStorage.getItem('textbook_chat_sessions') || '[]')
    const sessionIndex = sessionList.findIndex((s: ChatSession) => s.id === renameSessionId)
    if (sessionIndex >= 0) {
      sessionList[sessionIndex].title = renameValue.trim()
      localStorage.setItem('textbook_chat_sessions', JSON.stringify(sessionList))
      setSessions(sessionList)
      message.success('重命名成功')
    }
    setRenameModalVisible(false)
    setRenameSessionId('')
    setRenameValue('')
  }

  // 置顶/取消置顶
  const handleTogglePin = (id: string) => {
    const sessionList = JSON.parse(localStorage.getItem('textbook_chat_sessions') || '[]')
    const sessionIndex = sessionList.findIndex((s: ChatSession) => s.id === id)
    if (sessionIndex >= 0) {
      sessionList[sessionIndex].pinned = !sessionList[sessionIndex].pinned
      const sortedSessions = sessionList.sort((a: ChatSession, b: ChatSession) => {
        if (a.pinned && !b.pinned) return -1
        if (!a.pinned && b.pinned) return 1
        return b.timestamp - a.timestamp
      })
      localStorage.setItem('textbook_chat_sessions', JSON.stringify(sortedSessions))
      setSessions(sortedSessions)
      message.success(sessionList[sessionIndex].pinned ? '已置顶' : '已取消置顶')
    }
  }

  // 分享会话
  const handleShare = (id: string) => {
    const session = sessions.find(s => s.id === id)
    if (session) {
      const shareData = {
        title: session.title,
        messages: JSON.parse(localStorage.getItem(`textbook_chat_history_${id}`) || '[]'),
      }
      const shareText = JSON.stringify(shareData, null, 2)
      
      if (navigator.share) {
        navigator.share({
          title: session.title,
          text: shareText,
        }).catch(() => {
          navigator.clipboard.writeText(shareText)
          message.success('会话内容已复制到剪贴板')
        })
      } else {
        navigator.clipboard.writeText(shareText)
        message.success('会话内容已复制到剪贴板')
      }
    }
  }

  // 删除会话
  const handleDelete = (id: string) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个对话吗？此操作不可恢复。',
      onOk: () => {
        const sessionList = JSON.parse(localStorage.getItem('textbook_chat_sessions') || '[]')
        const filtered = sessionList.filter((s: ChatSession) => s.id !== id)
        localStorage.setItem('textbook_chat_sessions', JSON.stringify(filtered))
        localStorage.removeItem(`textbook_chat_history_${id}`)
        setSessions(filtered)
        if (id === sessionId) {
          handleNewChat()
        }
        message.success('删除成功')
      },
    })
  }

  // 获取会话菜单
  const getSessionMenu = (session: ChatSession): MenuProps => ({
    items: [
      {
        key: 'rename',
        label: '重命名',
        icon: <EditOutlined />,
        onClick: () => handleRename(session.id),
      },
      {
        key: 'pin',
        label: session.pinned ? '取消置顶' : '置顶',
        icon: <PushpinOutlined />,
        onClick: () => handleTogglePin(session.id),
      },
      {
        key: 'share',
        label: '分享',
        icon: <ShareAltOutlined />,
        onClick: () => handleShare(session.id),
      },
      {
        type: 'divider',
      },
      {
        key: 'delete',
        label: '删除',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => handleDelete(session.id),
      },
    ],
  })

  const { pinned, todaySessions, weekSessions, monthSessions, monthlyGroups } = getGroupedSessions()

  return (
    <div className="chat-layout">
      {/* 侧边栏 */}
      {sidebarVisible && (
        <div className="chat-sidebar">
          <div className="chat-sidebar-header">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              block
              onClick={handleNewChat}
              className="new-chat-button"
            >
              开启新对话
            </Button>
            <div className="chat-search">
              <Input
                prefix={<SearchOutlined />}
                placeholder="搜索对话..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
              />
            </div>
          </div>
          <div className="chat-sidebar-content">
            {/* 置顶会话 */}
            {pinned.length > 0 && (
              <div className="chat-session-group">
                <div className="chat-session-group-title">置顶</div>
                {pinned.map((session) => (
                  <div
                    key={session.id}
                    className={`chat-session-item ${session.id === sessionId ? 'active' : ''}`}
                    onClick={() => handleSwitchSession(session.id)}
                  >
                    <div className="chat-session-content">
                      <div className="chat-session-title">
                        {session.pinned && <PushpinOutlined className="pin-icon" />}
                        {session.title}
                      </div>
                      <div className="chat-session-preview">{session.lastMessage || '暂无消息'}</div>
                    </div>
                    <Dropdown menu={getSessionMenu(session)} trigger={['click']}>
                      <Button
                        type="text"
                        icon={<MoreOutlined />}
                        className="chat-session-menu"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Dropdown>
                  </div>
                ))}
              </div>
            )}

            {/* 今天 */}
            {todaySessions.length > 0 && (
              <div className="chat-session-group">
                <div className="chat-session-group-title">今天</div>
                {todaySessions.map((session) => (
                  <div
                    key={session.id}
                    className={`chat-session-item ${session.id === sessionId ? 'active' : ''}`}
                    onClick={() => handleSwitchSession(session.id)}
                  >
                    <div className="chat-session-content">
                      <div className="chat-session-title">{session.title}</div>
                      <div className="chat-session-preview">{session.lastMessage || '暂无消息'}</div>
                    </div>
                    <Dropdown menu={getSessionMenu(session)} trigger={['click']}>
                      <Button
                        type="text"
                        icon={<MoreOutlined />}
                        className="chat-session-menu"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Dropdown>
                  </div>
                ))}
              </div>
            )}

            {/* 7天内 */}
            {weekSessions.length > 0 && (
              <div className="chat-session-group">
                <div className="chat-session-group-title">7天内</div>
                {weekSessions.map((session) => (
                  <div
                    key={session.id}
                    className={`chat-session-item ${session.id === sessionId ? 'active' : ''}`}
                    onClick={() => handleSwitchSession(session.id)}
                  >
                    <div className="chat-session-content">
                      <div className="chat-session-title">{session.title}</div>
                      <div className="chat-session-preview">{session.lastMessage || '暂无消息'}</div>
                    </div>
                    <Dropdown menu={getSessionMenu(session)} trigger={['click']}>
                      <Button
                        type="text"
                        icon={<MoreOutlined />}
                        className="chat-session-menu"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Dropdown>
                  </div>
                ))}
              </div>
            )}

            {/* 30天内 */}
            {monthSessions.length > 0 && (
              <div className="chat-session-group">
                <div className="chat-session-group-title">30天内</div>
                {monthSessions.map((session) => (
                  <div
                    key={session.id}
                    className={`chat-session-item ${session.id === sessionId ? 'active' : ''}`}
                    onClick={() => handleSwitchSession(session.id)}
                  >
                    <div className="chat-session-content">
                      <div className="chat-session-title">{session.title}</div>
                      <div className="chat-session-preview">{session.lastMessage || '暂无消息'}</div>
                    </div>
                    <Dropdown menu={getSessionMenu(session)} trigger={['click']}>
                      <Button
                        type="text"
                        icon={<MoreOutlined />}
                        className="chat-session-menu"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Dropdown>
                  </div>
                ))}
              </div>
            )}

            {/* 按月分组 */}
            {Object.keys(monthlyGroups).map((monthKey) => {
              const [year, month] = monthKey.split('-')
              return (
                <div key={monthKey} className="chat-session-group">
                  <div className="chat-session-group-title">{`${year}年${parseInt(month)}月`}</div>
                  {monthlyGroups[monthKey].map((session) => (
                    <div
                      key={session.id}
                      className={`chat-session-item ${session.id === sessionId ? 'active' : ''}`}
                      onClick={() => handleSwitchSession(session.id)}
                    >
                      <div className="chat-session-content">
                        <div className="chat-session-title">{session.title}</div>
                        <div className="chat-session-preview">{session.lastMessage || '暂无消息'}</div>
                      </div>
                      <Dropdown menu={getSessionMenu(session)} trigger={['click']}>
                        <Button
                          type="text"
                          icon={<MoreOutlined />}
                          className="chat-session-menu"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Dropdown>
                    </div>
                  ))}
                </div>
              )
            })}

            {pinned.length === 0 && todaySessions.length === 0 && weekSessions.length === 0 && 
             monthSessions.length === 0 && Object.keys(monthlyGroups).length === 0 && (
              <div className="chat-empty-sidebar">
                <Text type="secondary">暂无对话记录</Text>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 主内容区 */}
      <div className="chat-content">
        <div className="chat-container" ref={chatContainerRef}>
          {/* 顶部工具栏 */}
          <div className="chat-header">
            <Button
              type="text"
              icon={sidebarVisible ? <CloseOutlined /> : <HistoryOutlined />}
              onClick={() => setSidebarVisible(!sidebarVisible)}
              className="sidebar-toggle"
            />
            <Text className="chat-header-title">
              {sessions.find(s => s.id === sessionId)?.title || '智能教材编写助手'}
            </Text>
            <Button
              type="text"
              icon={<BookOutlined />}
              onClick={() => navigate('/textbook/writing')}
              title="返回编写页面"
            />
          </div>
          {/* 消息列表 */}
          <div className="chat-messages">
            {messages.length === 0 ? (
              <div className="chat-empty">
                <div className="chat-empty-icon">
                  <RobotOutlined style={{ fontSize: 64, color: '#4A90E2' }} />
                </div>
                <Text className="chat-empty-title">Hi, 欢迎使用智能教材编写助手</Text>
                <Text className="chat-empty-subtitle">我是您的教材编写AI助手，可以根据您的需求生成高质量的教材内容</Text>
                <div className="chat-suggestions">
                  <div className="suggestion-card" onClick={() => {
                    if (loading) return // 防止重复点击
                    handleSend('请编写一本《中医基础理论》教材，面向本科学生')
                  }}>
                    <BookOutlined />
                    <div>
                      <Text strong>编写《中医基础理论》</Text>
                      <Text type="secondary">面向本科学生的教材</Text>
                    </div>
                  </div>
                  <div className="suggestion-card" onClick={() => {
                    if (loading) return // 防止重复点击
                    handleSend('编写《方剂学》教材，包含12章，每章6节')
                  }}>
                    <BookOutlined />
                    <div>
                      <Text strong>编写《方剂学》</Text>
                      <Text type="secondary">12章，每章6节</Text>
                    </div>
                  </div>
                  <div className="suggestion-card" onClick={() => {
                    if (loading) return // 防止重复点击
                    handleSend('编写《中药学》教材，面向硕士阶段学生')
                  }}>
                    <BookOutlined />
                    <div>
                      <Text strong>编写《中药学》</Text>
                      <Text type="secondary">面向硕士阶段</Text>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg) => (
                  <div key={msg.id} className={`chat-message ${msg.type}`}>
                    <Avatar
                      className="chat-avatar"
                      icon={msg.type === 'user' ? <UserOutlined /> : <RobotOutlined />}
                      style={{
                        backgroundColor: msg.type === 'user' ? '#4A90E2' : '#52C41A',
                      }}
                    />
                    <div className="chat-message-content">
                      <div className="chat-message-bubble">
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="chat-attachments">
                            {msg.attachments.map((att) => {
                              const handleFileClick = () => {
                                if (att.url) {
                                  window.open(att.url, '_blank')
                                } else {
                                  message.warning('文件 URL 不可用')
                                }
                              }
                              
                              return (
                                <div 
                                  key={att.id} 
                                  className="chat-attachment"
                                  onClick={handleFileClick}
                                  style={{ cursor: 'pointer' }}
                                  title={att.type === 'image' ? '点击查看大图' : '点击打开文件'}
                                >
                                  {att.type === 'image' && att.url ? (
                                    <img src={att.url} alt={att.name} className="attachment-image" />
                                  ) : (
                                    <div className="attachment-file">
                                      <FileTextOutlined />
                                      <Text>{att.name}</Text>
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                        
                        {/* 大纲显示 */}
                        {msg.stage === 'outline_generated' && msg.bookOutline && (
                          <div className="outline-display" style={{ marginTop: 16, padding: 16, background: '#F9F9F9', borderRadius: 8, border: '1px solid #E0E0E0' }}>
                            <div style={{ marginBottom: 12, fontSize: 16, fontWeight: 'bold' }}>
                              📋 书籍大纲
                            </div>
                            {msg.outlineMarkdown && (
                              <div>
                                {isEditingOutline && msg.id === messages[messages.length - 1]?.id ? (
                                  <TextArea
                                    value={editingOutlineContent || msg.outlineMarkdown}
                                    onChange={(e) => setEditingOutlineContent(e.target.value)}
                                    rows={15}
                                    style={{
                                      fontFamily: 'monospace',
                                      fontSize: 13,
                                      lineHeight: 1.6
                                    }}
                                    placeholder="在此直接编辑大纲内容..."
                                  />
                                ) : (
                                  <div 
                                    className="outline-markdown" 
                                    style={{ 
                                      maxHeight: '400px', 
                                      overflowY: 'auto', 
                                      padding: 12, 
                                      background: '#FFFFFF', 
                                      borderRadius: 4,
                                      whiteSpace: 'pre-wrap',
                                      fontFamily: 'monospace',
                                      fontSize: 13,
                                      lineHeight: 1.6,
                                      cursor: 'text'
                                    }}
                                    onClick={() => {
                                      setIsEditingOutline(true)
                                      setEditingOutlineContent(msg.outlineMarkdown || '')
                                    }}
                                    title="点击此处直接编辑大纲内容"
                                  >
                                    {msg.outlineMarkdown}
                                  </div>
                                )}
                              </div>
                            )}
                            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {isEditingOutline && msg.id === messages[messages.length - 1]?.id ? (
                                <>
                                  <Button 
                                    type="primary" 
                                    icon={<CheckOutlined />}
                                    onClick={() => handleConfirmOutline(undefined, editingOutlineContent)}
                                    disabled={loading}
                                  >
                                    确认编辑
                                  </Button>
                                  <Button 
                                    onClick={() => {
                                      setIsEditingOutline(false)
                                      setEditingOutlineContent('')
                                    }}
                                    disabled={loading}
                                  >
                                    取消编辑
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button 
                                    type="primary" 
                                    icon={<CheckOutlined />}
                                    onClick={() => handleConfirmOutline()}
                                    disabled={loading}
                                  >
                                    确认大纲
                                  </Button>
                                  <Button 
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                      setIsEditingOutline(true)
                                      setEditingOutlineContent(msg.outlineMarkdown || '')
                                    }}
                                    disabled={loading}
                                  >
                                    直接编辑
                                  </Button>
                                  <Button 
                                    icon={<EditOutlined />}
                                    onClick={() => setOutlineModificationModalVisible(true)}
                                    disabled={loading}
                                  >
                                    对话修改
                                  </Button>
                                </>
                              )}
                              <Dropdown
                                menu={{
                                  items: [
                                    {
                                      key: 'markdown',
                                      label: '下载 Markdown',
                                      icon: <FileMarkdownOutlined />,
                                      onClick: () => handleDownloadDocument('outline', 'markdown', {
                                        content: msg.outlineMarkdown,
                                        title: currentBookOutline?.title,
                                        author: currentBookOutline?.author,
                                      }),
                                    },
                                    {
                                      key: 'docx',
                                      label: '下载 DOCX',
                                      icon: <FileWordOutlined />,
                                      onClick: () => handleDownloadDocument('outline', 'docx', {
                                        content: msg.outlineMarkdown,
                                        title: currentBookOutline?.title,
                                        author: currentBookOutline?.author,
                                      }),
                                    },
                                    {
                                      key: 'pdf',
                                      label: '下载 PDF',
                                      icon: <FilePdfOutlined />,
                                      onClick: () => handleDownloadDocument('outline', 'pdf', {
                                        content: msg.outlineMarkdown,
                                        title: currentBookOutline?.title,
                                        author: currentBookOutline?.author,
                                      }),
                                    },
                                  ],
                                }}
                                trigger={['click']}
                              >
                                <Button icon={<DownloadOutlined />}>
                                  下载大纲
                                </Button>
                              </Dropdown>
                            </div>
                          </div>
                        )}

                        {/* 章节显示：头部编号与内容中的实际章节一致 */}
                        {msg.stage === 'chapter_generated' && msg.chapterContent && (
                          <div className="chapter-display" style={{ marginTop: 16, padding: 16, background: '#F9F9F9', borderRadius: 8, border: '2px solid #1890ff' }}>
                            <div style={{ marginBottom: 12, fontSize: 16, fontWeight: 'bold', color: '#1890ff' }}>
                              📖 {(() => {
                                const fromContent = parseChapterFromContent(msg.chapterContent || msg.chapterMarkdown || '')
                                const displayNum = fromContent?.number ?? msg.chapterNumber
                                const displayTitle = fromContent?.title ?? msg.chapterTitle
                                return formatChapterTitle(displayNum, displayTitle)
                              })()}
                            </div>
                            <div style={{ marginBottom: 12, padding: 8, background: '#E6F7FF', borderRadius: 4, fontSize: 13, color: '#0050B3' }}>
                              💡 提示：您可以查看章节内容，下载三种格式（Markdown、DOCX、PDF），编辑或修改后确认。确认后将自动生成下一章。
                            </div>
                            {isEditingChapter && msg.id === messages[messages.length - 1]?.id ? (
                              <TextArea
                                value={editingChapterContent || msg.chapterMarkdown || msg.chapterContent || ''}
                                onChange={(e) => setEditingChapterContent(e.target.value)}
                                rows={20}
                                style={{
                                  fontFamily: 'monospace',
                                  fontSize: 13,
                                  lineHeight: 1.6
                                }}
                                placeholder="在此直接编辑章节内容..."
                              />
                            ) : (
                              <div 
                                className="chapter-content" 
                                style={{ 
                                  maxHeight: '500px', 
                                  overflowY: 'auto', 
                                  padding: 12, 
                                  background: '#FFFFFF', 
                                  borderRadius: 4,
                                  whiteSpace: 'pre-wrap',
                                  fontFamily: 'monospace',
                                  fontSize: 13,
                                  lineHeight: 1.6,
                                  cursor: 'text'
                                }}
                                onClick={() => {
                                  setIsEditingChapter(true)
                                  setEditingChapterContent(msg.chapterMarkdown || msg.chapterContent || '')
                                }}
                                title="点击此处直接编辑章节内容"
                              >
                                {msg.chapterMarkdown || msg.chapterContent}
                              </div>
                            )}
                            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {isEditingChapter && msg.id === messages[messages.length - 1]?.id ? (
                                <>
                                  <Button 
                                    type="primary" 
                                    icon={<CheckOutlined />}
                                    onClick={() => {
                                      setCurrentChapterData({
                                        number: msg.chapterNumber || 1,
                                        title: msg.chapterTitle || '',
                                        content: msg.chapterContent || ''
                                      })
                                      handleConfirmChapter(undefined, true, editingChapterContent)
                                    }}
                                    disabled={loading}
                                  >
                                    确认编辑并继续
                                  </Button>
                                  <Button 
                                    icon={<CheckOutlined />}
                                    onClick={() => {
                                      setCurrentChapterData({
                                        number: msg.chapterNumber || 1,
                                        title: msg.chapterTitle || '',
                                        content: msg.chapterContent || ''
                                      })
                                      handleConfirmChapter(undefined, false, editingChapterContent)
                                    }}
                                    disabled={loading}
                                  >
                                    确认编辑
                                  </Button>
                                  <Button 
                                    onClick={() => {
                                      setIsEditingChapter(false)
                                      setEditingChapterContent('')
                                    }}
                                    disabled={loading}
                                  >
                                    取消编辑
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button 
                                    type="primary" 
                                    icon={<CheckOutlined />}
                                    onClick={() => {
                                      setCurrentChapterData({
                                        number: msg.chapterNumber || 1,
                                        title: msg.chapterTitle || '',
                                        content: msg.chapterContent || ''
                                      })
                                      handleConfirmChapter(undefined, true)
                                    }}
                                    disabled={loading}
                                  >
                                    确认并继续下一章
                                  </Button>
                                  <Button 
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                      setIsEditingChapter(true)
                                      setEditingChapterContent(msg.chapterMarkdown || msg.chapterContent || '')
                                      setCurrentChapterData({
                                        number: msg.chapterNumber || 1,
                                        title: msg.chapterTitle || '',
                                        content: msg.chapterContent || ''
                                      })
                                    }}
                                    disabled={loading}
                                  >
                                    直接编辑
                                  </Button>
                                  <Button 
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                      setCurrentChapterData({
                                        number: msg.chapterNumber || 1,
                                        title: msg.chapterTitle || '',
                                        content: msg.chapterContent || ''
                                      })
                                      setChapterModificationModalVisible(true)
                                    }}
                                    disabled={loading}
                                  >
                                    对话修改
                                  </Button>
                                  <Button 
                                    onClick={() => {
                                      setCurrentChapterData({
                                        number: msg.chapterNumber || 1,
                                        title: msg.chapterTitle || '',
                                        content: msg.chapterContent || ''
                                      })
                                      handleConfirmChapter(undefined, false)
                                    }}
                                    disabled={loading}
                                  >
                                    仅确认（不继续）
                                  </Button>
                                </>
                              )}
                              <Dropdown
                                menu={{
                                  items: [
                                    {
                                      key: 'markdown',
                                      label: '📄 下载 Markdown (.md)',
                                      icon: <FileMarkdownOutlined />,
                                      onClick: async () => {
                                        try {
                                          const parsed = parseChapterFromContent(msg.chapterContent || msg.chapterMarkdown || '')
                                          await handleDownloadDocument('chapter', 'markdown', {
                                            content: msg.chapterMarkdown || msg.chapterContent,
                                            chapterNumber: parsed?.number ?? msg.chapterNumber,
                                            chapterTitle: parsed?.title ?? msg.chapterTitle,
                                            author: currentBookOutline?.author,
                                          })
                                          message.success('Markdown 格式下载成功')
                                        } catch (error: any) {
                                          message.error(`下载失败: ${error.message || '未知错误'}`)
                                        }
                                      },
                                    },
                                    {
                                      key: 'docx',
                                      label: '📝 下载 DOCX (.docx)',
                                      icon: <FileWordOutlined />,
                                      onClick: async () => {
                                        try {
                                          const parsed = parseChapterFromContent(msg.chapterContent || msg.chapterMarkdown || '')
                                          await handleDownloadDocument('chapter', 'docx', {
                                            content: msg.chapterMarkdown || msg.chapterContent,
                                            chapterNumber: parsed?.number ?? msg.chapterNumber,
                                            chapterTitle: parsed?.title ?? msg.chapterTitle,
                                            author: currentBookOutline?.author,
                                          })
                                          message.success('DOCX 格式下载成功')
                                        } catch (error: any) {
                                          message.error(`下载失败: ${error.message || '未知错误'}`)
                                        }
                                      },
                                    },
                                    {
                                      key: 'pdf',
                                      label: '📕 下载 PDF (.pdf)',
                                      icon: <FilePdfOutlined />,
                                      onClick: async () => {
                                        try {
                                          const parsed = parseChapterFromContent(msg.chapterContent || msg.chapterMarkdown || '')
                                          await handleDownloadDocument('chapter', 'pdf', {
                                            content: msg.chapterMarkdown || msg.chapterContent,
                                            chapterNumber: parsed?.number ?? msg.chapterNumber,
                                            chapterTitle: parsed?.title ?? msg.chapterTitle,
                                            author: currentBookOutline?.author,
                                          })
                                          message.success('PDF 格式下载成功')
                                        } catch (error: any) {
                                          message.error(`下载失败: ${error.message || '未知错误'}`)
                                        }
                                      },
                                    },
                                  ],
                                }}
                                trigger={['click']}
                              >
                                <Button 
                                  type="default"
                                  icon={<DownloadOutlined />}
                                  disabled={loading}
                                  style={{ borderColor: '#1890ff', color: '#1890ff' }}
                                >
                                  下载章节（Markdown / DOCX / PDF）
                                </Button>
                              </Dropdown>
                            </div>
                          </div>
                        )}

                        {/* 完成状态 - 显示完整书籍下载 */}
                        {msg.stage === 'completed' && (
                          <div style={{ marginTop: 16, padding: 16, background: '#E6F7FF', borderRadius: 8, border: '1px solid #91D5FF' }}>
                            <div style={{ marginBottom: 12, fontSize: 16, fontWeight: 'bold', color: '#1890FF' }}>
                              ✅ {msg.content}
                            </div>
                            {generatedChapters.size > 0 && (
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <Dropdown
                                  menu={{
                                    items: [
                                      {
                                        key: 'markdown',
                                        label: '下载 Markdown',
                                        icon: <FileMarkdownOutlined />,
                                        onClick: () => handleDownloadDocument('book', 'markdown'),
                                      },
                                      {
                                        key: 'docx',
                                        label: '下载 DOCX',
                                        icon: <FileWordOutlined />,
                                        onClick: () => handleDownloadDocument('book', 'docx'),
                                      },
                                      {
                                        key: 'pdf',
                                        label: '下载 PDF',
                                        icon: <FilePdfOutlined />,
                                        onClick: () => handleDownloadDocument('book', 'pdf'),
                                      },
                                    ],
                                  }}
                                  trigger={['click']}
                                >
                                  <Button type="primary" icon={<DownloadOutlined />} size="large">
                                    下载完整书籍
                                  </Button>
                                </Dropdown>
                              </div>
                            )}
                          </div>
                        )}

                        {/* 普通消息内容 */}
                        {(!msg.stage || (msg.stage !== 'outline_generated' && msg.stage !== 'chapter_generated' && msg.stage !== 'completed')) && (
                          <div className="chat-message-text">{msg.content}</div>
                        )}
                      </div>
                      <Text className="chat-message-time">
                        {new Date(msg.timestamp).toLocaleTimeString('zh-CN', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Text>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="chat-message assistant">
                    <Avatar
                      className="chat-avatar"
                      icon={<RobotOutlined />}
                      style={{ backgroundColor: '#52C41A' }}
                    />
                    <div className="chat-message-content">
                      <div className="chat-message-bubble">
                        <Spin size="small" />
                        <Text style={{ marginLeft: 8 }}>正在思考...</Text>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* 输入区域 */}
          <div className="chat-input-area textbook-writing-input-area">
            {fileList.length > 0 && (
              <div className="chat-file-list">
                {fileList.map((file) => {
                  const handleFileClick = () => {
                    if (file.status !== 'done') {
                      message.warning('文件尚未准备就绪')
                      return
                    }
                    
                    const attachment = (file as any).attachment as Attachment
                    const fileObj = (file as any).fileObj as File
                    const fileUrl = (file as any).fileUrl as string
                    
                    let url = attachment?.url || fileUrl
                    
                    if (!url && fileObj) {
                      url = URL.createObjectURL(fileObj)
                      if (attachment) {
                        attachment.url = url
                      }
                    }
                    
                    if (url) {
                      window.open(url, '_blank')
                    } else {
                      message.warning('无法打开文件：文件数据不可用')
                    }
                  }
                  
                  return (
                    <div key={file.uid} className="chat-file-item">
                      <div className="chat-file-icon">
                        {file.type?.startsWith('image/') ? (
                          <PictureOutlined />
                        ) : file.type?.startsWith('audio/') ? (
                          <AudioOutlined />
                        ) : (
                          <FileTextOutlined />
                        )}
                      </div>
                      <div 
                        className="chat-file-info"
                        onClick={handleFileClick}
                        style={{ 
                          cursor: (file.status === 'done' || (file as any).attachment) ? 'pointer' : 'default',
                          flex: 1
                        }}
                        title={(file.status === 'done' || (file as any).attachment) ? '点击打开文件' : undefined}
                      >
                        <Text ellipsis style={{ maxWidth: 200, display: 'block' }}>
                          {file.name}
                        </Text>
                        {file.status === 'uploading' && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            上传中... {file.percent !== undefined && file.percent !== null ? file.percent : 0}%
                          </Text>
                        )}
                        {file.status === 'error' && (
                          <Text type="danger" style={{ fontSize: 12 }}>
                            上传失败
                          </Text>
                        )}
                        {file.status === 'done' && (
                          <Text type="success" style={{ fontSize: 12 }}>
                            上传完成
                          </Text>
                        )}
                      </div>
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemove(file)
                        }}
                        disabled={file.status === 'uploading' && file.percent !== undefined && file.percent < 100}
                      />
                    </div>
                  )
                })}
              </div>
            )}
            {isRecording && (
              <div className="recording-indicator chat-recording-above-input">
                <div className="recording-dot"></div>
                <Text type="danger" style={{ fontSize: 12 }}>
                  正在录制: {formatRecordingTime(recordingTime)}
                </Text>
              </div>
            )}
            <div className="textbook-composer-wrap">
            <div
              className="chat-input-wrapper textbook-chat-composer"
              onMouseDown={(e) => {
                const t = e.target as HTMLElement
                if (t.closest('button') || t.closest('.ant-upload') || t.closest('input[type="file"]')) return
                if (t.tagName === 'TEXTAREA' || t.closest('textarea')) return
                chatTextareaRef.current?.focus()
              }}
            >
              <div className="chat-input-column">
                {composerHint && (
                  <div className="composer-hint" role="status">
                    {composerHint}
                  </div>
                )}
                <div className="chat-textarea-shell">
                  <TextArea
                    ref={chatTextareaRef}
                    className="chat-textarea"
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value)
                      setComposerHint(null)
                    }}
                    onKeyPress={handleKeyPress}
                    placeholder={
                      activeWritingMode === 'outline'
                        ? '目录模式：输入书名与要求，发送后仅生成目录（支持连续多本）'
                        : currentStage === 'initial'
                        ? '描述您想要编写的教材...'
                        : currentStage === 'outline_generated'
                        ? '可补充说明或备忘；改目录请用「对话修改」，确认请点「确认大纲」'
                        : currentStage === 'outline_confirmed'
                        ? '可输入补充说明；撰写正文点「撰写书籍内容」，改目录用「对话修改」'
                        : currentStage === 'chapter_generating'
                        ? '生成中可继续输入备忘，或点右侧停止后在此继续编辑'
                        : currentStage === 'chapter_generated'
                        ? '章节已生成，请在上方查看并确认；也可在此输入备忘'
                        : currentStage === 'completed'
                        ? '所有章节已生成完成，您可以继续对话或开始新的书籍生成...'
                        : '输入消息...'
                    }
                    rows={1}
                    autoSize={{ minRows: 1, maxRows: 1 }}
                  />
                </div>
                <div className="chat-input-bottom-row">
                <div className="chat-input-toolbar" role="toolbar" aria-label="教材编写快捷操作">
                  {(!activeWritingMode || activeWritingMode === 'outline') && (
                  <div
                    className={`chat-toolbar-pill ${
                      activeWritingMode === 'outline' ? 'chat-toolbar-pill--active' : ''
                    }`}
                  >
                    <button
                      type="button"
                      className="chat-toolbar-pill-main"
                      disabled={loading}
                      onClick={handleToolbarWriteOutline}
                    >
                      <UnorderedListOutlined />
                      <span>撰写书籍目录</span>
                    </button>
                    {activeWritingMode === 'outline' && (
                      <button
                        type="button"
                        className="chat-toolbar-pill-close"
                        aria-label="取消选中"
                        onClick={(e) => {
                          e.stopPropagation()
                          setActiveWritingMode(null)
                          setComposerHint(null)
                        }}
                      >
                        <CloseOutlined />
                      </button>
                    )}
                  </div>
                  )}
                  {(!activeWritingMode || activeWritingMode === 'content') && (
                  <div
                    className={`chat-toolbar-pill ${
                      activeWritingMode === 'content' ? 'chat-toolbar-pill--active' : ''
                    }`}
                  >
                    <button
                      type="button"
                      className="chat-toolbar-pill-main"
                      disabled={loading}
                      onClick={handleToolbarWriteContent}
                    >
                      <FileTextOutlined />
                      <span>撰写书籍内容</span>
                    </button>
                    {activeWritingMode === 'content' && (
                      <button
                        type="button"
                        className="chat-toolbar-pill-close"
                        aria-label="取消选中"
                        onClick={(e) => {
                          e.stopPropagation()
                          setActiveWritingMode(null)
                          setComposerHint(null)
                        }}
                      >
                        <CloseOutlined />
                      </button>
                    )}
                  </div>
                  )}
                  {(!activeWritingMode || activeWritingMode === 'cover') && (
                  <div
                    className={`chat-toolbar-pill ${
                      activeWritingMode === 'cover' ? 'chat-toolbar-pill--active' : ''
                    }`}
                  >
                    <button
                      type="button"
                      className="chat-toolbar-pill-main"
                      disabled={loading}
                      onClick={handleToolbarWriteCover}
                    >
                      <PictureOutlined />
                      <span>生成书籍封面</span>
                    </button>
                    {activeWritingMode === 'cover' && (
                      <button
                        type="button"
                        className="chat-toolbar-pill-close"
                        aria-label="取消选中"
                        onClick={(e) => {
                          e.stopPropagation()
                          setActiveWritingMode(null)
                          setComposerHint(null)
                        }}
                      >
                        <CloseOutlined />
                      </button>
                    )}
                  </div>
                  )}
                </div>
                <div className="chat-input-bottom-actions">
                  <Upload
                    customRequest={handleUpload}
                    showUploadList={false}
                    accept="image/*,audio/*,.pdf,.doc,.docx,.txt,.xls,.xlsx"
                    multiple
                  >
                    <Button
                      type="text"
                      icon={<PaperClipOutlined />}
                      title="上传文件"
                      className="chat-attach-button"
                    />
                  </Upload>
                  {!isRecording ? (
                    <Button
                      type="text"
                      icon={<AudioOutlined />}
                      title="语音输入"
                      onClick={handleStartRecording}
                      className="voice-record-button chat-voice-round"
                    />
                  ) : (
                    <Button
                      type="text"
                      icon={<StopOutlined />}
                      title="停止录制"
                      onClick={handleStopRecording}
                      danger
                      className="voice-stop-button chat-voice-round"
                    />
                  )}
                  {loading ? (
                    <Button
                      type="primary"
                      shape="circle"
                      className="chat-stop-generate-button"
                      title="停止生成"
                      aria-label="停止生成"
                      onClick={handleStopConversation}
                    >
                      <span className="chat-stop-generate-button__square" aria-hidden />
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={() => handleSend()}
                      disabled={!inputValue.trim()}
                      className="chat-send-button chat-send-button--inline"
                    >
                      发送
                    </Button>
                  )}
                </div>
                </div>
              </div>
            </div>
            </div>
            <div className="chat-footer">
              <Text type="secondary" style={{ fontSize: 12 }}>
                内容由AI生成，符合规则库标准，仅供参考
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* 重命名模态框 */}
      <Modal
        title="重命名对话"
        open={renameModalVisible}
        onOk={handleRenameConfirm}
        onCancel={() => {
          setRenameModalVisible(false)
          setRenameValue('')
        }}
        okText="确定"
        cancelText="取消"
      >
        <AntInput
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          placeholder="请输入对话名称"
          onPressEnter={handleRenameConfirm}
          autoFocus
        />
      </Modal>

      {/* 大纲修改模态框 */}
      <Modal
        title="修改大纲"
        open={outlineModificationModalVisible}
        onOk={() => {
          if (outlineModificationText.trim()) {
            handleConfirmOutline(outlineModificationText)
          } else {
            handleConfirmOutline()
          }
        }}
        onCancel={() => {
          setOutlineModificationModalVisible(false)
          setOutlineModificationText('')
        }}
        okText="确认修改"
        cancelText="取消"
        width={600}
        modalRender={(modal) => (
          <div
            ref={outlineModalRef}
            style={{
              transform: outlineModalPosition.x || outlineModalPosition.y 
                ? `translate(${outlineModalPosition.x}px, ${outlineModalPosition.y}px)` 
                : 'none',
              cursor: outlineDragStartRef.current ? 'grabbing' : 'default'
            }}
            onMouseDown={handleOutlineModalMouseDown}
          >
            {modal}
          </div>
        )}
        styles={{
          body: { padding: '24px' },
          header: { cursor: 'grab', userSelect: 'none' }
        }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          请描述您希望如何修改大纲：
        </Text>
        <TextArea
          value={outlineModificationText}
          onChange={(e) => setOutlineModificationText(e.target.value)}
          placeholder="例如：增加第13章关于针灸的内容，修改第5章的标题为..."
          rows={6}
          autoFocus
        />
      </Modal>

      {/* 章节修改模态框 */}
      <Modal
        title={currentChapterData?.number ? `修改${formatChapterTitle(currentChapterData.number, currentChapterData.title)}` : '修改章节'}
        open={chapterModificationModalVisible}
        onOk={() => {
          if (chapterModificationText.trim()) {
            handleConfirmChapter(chapterModificationText, true)
          } else {
            handleConfirmChapter(undefined, true)
          }
        }}
        onCancel={() => {
          setChapterModificationModalVisible(false)
          setChapterModificationText('')
        }}
        okText="确认修改并继续"
        cancelText="取消"
        width={600}
        modalRender={(modal) => (
          <div
            ref={chapterModalRef}
            style={{
              transform: chapterModalPosition.x || chapterModalPosition.y 
                ? `translate(${chapterModalPosition.x}px, ${chapterModalPosition.y}px)` 
                : 'none',
              cursor: chapterDragStartRef.current ? 'grabbing' : 'default'
            }}
            onMouseDown={handleChapterModalMouseDown}
          >
            {modal}
          </div>
        )}
        styles={{
          body: { padding: '24px' },
          header: { cursor: 'grab', userSelect: 'none' }
        }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          请描述您希望如何修改本章节：
        </Text>
        <TextArea
          value={chapterModificationText}
          onChange={(e) => setChapterModificationText(e.target.value)}
          placeholder="例如：增加更多关于...的内容，修改第3节的表述..."
          rows={6}
          autoFocus
        />
      </Modal>
    </div>
  )
}

export default TextbookWritingChatPage

